
class Modal {
  constructor(containerId) {
    this.container = document.body; // Always append to body to avoid stacking context issues
    this.modalElement = null;
    this._boundClose = this.close.bind(this);
    this._boundKeydown = this._onKeydown.bind(this);
    this.mobileMediaQuery = window.matchMedia("(max-width: 768px)");
  }
  open(templateId, ariaLabel) {
    if (this.modalElement) this.close();
    const template = document.getElementById(templateId);
    if (!template) {
      return null;
    }
    // Save the element that triggered the modal so we can restore focus on close
    this._triggerElement = document.activeElement;

    this.modalElement = document.createElement('div');
    this.modalElement.classList.add('modal-overlay');
    if (this.mobileMediaQuery.matches) {
      this.modalElement.classList.add('is-mobile');
    }
    const ariaLabelAttr = ariaLabel ? ` aria-label="${ariaLabel}"` : '';
    this.modalElement.innerHTML = `<div class="modal-content" role="dialog" aria-modal="true"${ariaLabelAttr}>${template.innerHTML}</div>`;
    this.container.appendChild(this.modalElement);
    document.body.classList.add('modal-open');

    // Close on overlay click (clicking outside the modal content)
    this.modalElement.addEventListener('click', (e) => {
      if (e.target === this.modalElement) {
        this.close();
      }
    });

    this.modalElement.querySelector('.close-button').addEventListener('click', this._boundClose);
    document.addEventListener('keydown', this._boundKeydown);

    // Focus the close button so screen readers announce the dialog
    const closeBtn = this.modalElement.querySelector('.close-button');
    if (closeBtn) requestAnimationFrame(() => closeBtn.focus());

    // Scroll to position the gallery for better mock visibility (both mobile and desktop)
    this._scrollToGallery();

    return this.modalElement.querySelector('.modal-content');
  }

  _scrollToGallery() {
    // Find the media gallery element
    const gallery = document.querySelector('media-gallery, .media-gallery, [data-media-gallery]');
    if (gallery) {
      // Small delay to ensure modal is fully rendered
      requestAnimationFrame(() => {
        // Switch to first slide (product mock)
        if (gallery.slideshow && typeof gallery.slideshow.select === 'function') {
          gallery.slideshow.select(0, undefined, { animate: false });
        }
        // Scroll to just below the header (dynamic height via CSS variable or measurement)
        const header = document.querySelector('header, [role="banner"]');
        const headerHeight = header ? header.getBoundingClientRect().bottom : 0;
        window.scrollTo({
          top: Math.max(0, headerHeight),
          behavior: 'smooth'
        });
      });
    }
  }
  close() {
    if (!this.modalElement) return;
    const onClose = this._onCloseCallback;
    this._onCloseCallback = null;
    const trigger = this._triggerElement;
    this._triggerElement = null;
    this.container.removeChild(this.modalElement);
    this.modalElement = null;
    document.body.classList.remove('modal-open');
    document.removeEventListener('keydown', this._boundKeydown);
    // Restore focus to the element that opened the modal
    if (trigger && typeof trigger.focus === 'function') trigger.focus();
    if (onClose) onClose();
  }
  _onKeydown(event) {
    if (event.key === 'Escape') this.close();
    // Focus trap: keep Tab cycling within the modal
    if (event.key === 'Tab' && this.modalElement) {
      const modalContent = this.modalElement.querySelector('.modal-content');
      if (!modalContent) return;
      const focusable = modalContent.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  }
}

// Fallback hex when color_value is empty
const FRAME_COLOR_HEX = {
  black: '#2d2d2d', white: '#f5f0e8', natural: '#c4a574',
  brown: '#6b4226', gold: '#c9a84c', silver: '#b8b8b8',
};

class SkuPicker {
  // Cart-line properties OWNED by personalise-canvas.js (the personalisable
  // template's design link + render references). _updateFormProperties must
  // preserve these when it rebuilds the sku-picker's own option properties,
  // or a personalised line item loses its artwork at add-to-cart.
  static PERSONALISE_PROPERTY_KEYS = new Set([
    '_design_id',
    '_design_template_id',
    '_photo_s3_key',
    '_styled_photo_s3_key',
    '_rendered_image_url',
    '_preview_image_url',
    '_preview_image_s3_key',
  ]);

  constructor(containerId, options = {}) {
    this.container = document.getElementById(containerId);
    if (!this.container) { console.warn('[SkuPicker] Container not found:', containerId); return; }

    // Check if we need to lazy load configurator data via GraphQL
    // If VariantConfigurators doesn't exist but VariantConfiguratorsMetadata does, trigger lazy loading
    const variantConfiguratorsExists = document.getElementById('VariantConfigurators');
    const metadataExists = document.getElementById('VariantConfiguratorsMetadata');
    console.log('[SkuPicker] Init:', { container: !!this.container, configurators: !!variantConfiguratorsExists, metadata: !!metadataExists });

    if (!variantConfiguratorsExists && metadataExists) {
      this._retryCount = 0;
      this._containerId = containerId;
      this._options = options;
      this._attemptLazyLoad();
      return;
    }

    this._initialize();
    window.dispatchEvent(new CustomEvent('sku-picker:ready'));
  }

  _attemptLazyLoad() {
    this._retryCount++;
    this._lazyLoadConfiguratorsViaGraphQL().then(() => {
      this._initialize();
      window.dispatchEvent(new CustomEvent('sku-picker:ready'));
      this._backgroundLoadArticleContent();
    }).catch((error) => {
      console.error('[SkuPicker] Failed to lazy load configurators:', error);
      const maxRetries = 2;
      if (this._retryCount < maxRetries) {
        // Auto-retry once after a short delay
        const delay = this._retryCount * 2000;
        console.log(`[SkuPicker] Auto-retrying in ${delay}ms (attempt ${this._retryCount + 1}/${maxRetries})`);
        setTimeout(() => this._attemptLazyLoad(), delay);
      } else {
        // Show error with manual retry button
        this.container.innerHTML = `<div style="padding: 20px; text-align: center;">
          <p style="color: #999; margin: 0 0 12px;">Failed to load product options.</p>
          <button style="padding: 8px 20px; border: 1px solid #ccc; background: #fff; border-radius: 4px; cursor: pointer; font-size: 14px;" onclick="this.parentElement.innerHTML='<p style=\\'color:#999;text-align:center\\'>Loading...</p>'; new SkuPicker('${this._containerId}', ${JSON.stringify(this._options)});">Try again</button>
        </div>`;
      }
    });
  }

  /**
   * Lazy load configurators via GraphQL Storefront API
   */
  async _lazyLoadConfiguratorsViaGraphQL() {
    // Read the metadata that was server-rendered
    const metadataElement = document.getElementById('VariantConfiguratorsMetadata');
    if (!metadataElement) {
      throw new Error('VariantConfiguratorsMetadata not found');
    }

    const metadata = JSON.parse(metadataElement.textContent);
    const variantGids = metadata.variantConfiguratorGids;
    const variantArtProducts = metadata.variantArtProducts || {};

    // Store croppable ratio early so it's available for _buildVariantConfiguratorData
    this.croppableRatio = metadata.croppableRatio ?? 1.0;
    console.log('[SkuPicker] croppableRatio from metadata:', this.croppableRatio, 'raw:', metadata.croppableRatio);

    // Get locale from metadata with fallback
    let locale = metadata.locale || 'en-US';

    // Fallback to HTML lang attribute if metadata doesn't have locale
    if (!metadata.locale && document.documentElement.lang) {
      locale = document.documentElement.lang;
    }

    const translations = metadata.sku_translations || {};

    if (!variantGids || Object.keys(variantGids).length === 0) {
      throw new Error('No configurator GIDs found');
    }

    // Fetch all configurators via GraphQL
    const configurators = await this._fetchConfiguratorsViaGraphQL(Object.values(variantGids));

    // Build VariantConfigurators object
    const variantConfigurators = {
      variants: {},
      money_format: metadata.money_format,
      market_handle: metadata.market_handle,
      country_code: metadata.country_code,
      discount_percent: metadata.discount_percent,
      discount_expiry_hour: metadata.discount_expiry_hour,
      discount_expiry_date: metadata.discount_expiry_date,
      defaultUnit: metadata.defaultUnit,
      ui_strings: metadata.ui_strings,
      tiered_discount_config: metadata.tiered_discount_config
    };

    // Map configurators back to variant IDs
    for (const [variantId, gid] of Object.entries(variantGids)) {
      const configurator = configurators[gid];
      if (configurator) {
        const configData = this._buildVariantConfiguratorData(configurator, metadata.market_handle, locale, translations);
        configData.artProduct = variantArtProducts[variantId] || null;
        variantConfigurators.variants[variantId] = configData;
      }
    }

    // Keep _rawGuideRefs on variants — article content is fetched on-demand
    // when user opens a guide modal (deferred out of critical path)

    // Inject the built VariantConfigurators into the DOM
    const script = document.createElement('script');
    script.id = 'VariantConfigurators';
    script.type = 'application/json';
    script.textContent = JSON.stringify(variantConfigurators);
    document.body.appendChild(script);
  }

  /**
   * Initialize the SKU picker (called after data is loaded)
   */
  _initialize() {
    // Load variant configurators
    this.variantConfiguratorsElement = document.getElementById('VariantConfigurators');
    if (!this.variantConfiguratorsElement) {
      console.warn('[SkuPicker] VariantConfigurators not found');
      this.container.innerHTML = '<div style="padding: 20px; color: #999; text-align: center;">This product does not have a configurator.</div>';
      return;
    }

    this.variantConfigurators = JSON.parse(this.variantConfiguratorsElement.textContent);
    this.currentVariantId = null;
    this.data = null;

    // Load locale and translations for UI strings
    const metadataElement = document.getElementById('VariantConfiguratorsMetadata');
    if (metadataElement) {
      const metadata = JSON.parse(metadataElement.textContent);
      this.locale = metadata.locale || 'en-US';
      this.translations = metadata.sku_translations || {};
      this.uiStrings = metadata.ui_strings || {};
      // Store croppable ratio for size filtering (artwork bleed/crop safety)
      this.croppableRatio = metadata.croppableRatio ?? 1.0;
    } else {
      this.locale = 'en-US';
      this.translations = {};
      this.uiStrings = {};
      this.croppableRatio = 1.0;
    }

    // Load Prodigi configuration lookup data
    this.prodigiConfigElement = document.getElementById('ProdigiConfig');

    // Parse and validate the config
    let parsedConfig = null;
    if (this.prodigiConfigElement) {
      try {
        parsedConfig = JSON.parse(this.prodigiConfigElement.textContent);
      } catch (e) {
        console.error('[ProdigiConfig] Failed to parse JSON:', e);
      }
    }

    // Ensure we have a valid array (handle null, undefined, or non-array values)
    this.prodigiConfig = Array.isArray(parsedConfig) ? parsedConfig : [];

    this.modal = new Modal('modal-container');
    this.state = { confirmed: {}, temporary: {} };
    this.currentSelectionDetails = { available: false, price: 0 };
    this.components = { size: {}, framing: {}, border: {} };
    this.moneyFormat = this.variantConfigurators.money_format;
    this.hasConfiguratorLoaded = false; // Track if a configurator is currently loaded
    this.currentVariantAvailable = true; // Track if current variant is in stock
    this.activeStyleName = null; // Set by style-picker when a named preset is active (null = custom/no style picker)
    this._abortController = new AbortController();

    // Initialize unit preference (from localStorage or default from localization)
    this._initializeUnitPreference();

    // Listen for unit toggles from anywhere else on the page (e.g. the surgical
    // PDP's <pdp-size-picker>). Without this, `this.currentUnit` only ever
    // updates when the legacy UI's own toggle is clicked — so the size
    // attached to ATC drifts from what the buyer saw on the PDP. Guard
    // against re-entry from our own dispatch.
    document.addEventListener('sku-picker:unit-changed', (e) => {
      const next = e.detail?.unit;
      if (next !== 'in' && next !== 'cm') return;
      if (next === this.currentUnit) return;
      this.currentUnit = next;
      try { localStorage.setItem('skuPicker_unitPreference', next); } catch {}
    });

    // Setup event delegation once (handles all option-main, remove-frame, info-link clicks)
    this._setupEventDelegation();

    // Detect initial variant from URL or form
    this._detectInitialVariant();

    // Listen for variant changes from Shopify variant selector
    this._setupVariantListener();

    // Initialize with current variant
    if (this.currentVariantId) {
      this._loadVariantConfigurator(this.currentVariantId);
    } else {
      this.container.innerHTML = '<div style="padding: 20px; color: #999; text-align: center;">This variant does not have a configurator.</div>';
      this.hasConfiguratorLoaded = false;
    }
  }

  /**
   * Tear down this instance: abort all event listeners, cancel pending rAFs,
   * and close any open modal. Called by pdp-ab-test.js before re-creating
   * the instance after a morph.
   */
  destroy() {
    if (this._abortController) {
      this._abortController.abort();
    }
    if (this._variantChangeTimeout) {
      cancelAnimationFrame(this._variantChangeTimeout);
      this._variantChangeTimeout = null;
    }
    if (this._updateUITimeout) {
      cancelAnimationFrame(this._updateUITimeout);
      this._updateUITimeout = null;
    }
    if (this.modal) {
      this.modal.close();
    }
  }

  /**
   * Fetch configurators via GraphQL Storefront API
   * @param {Array<string>} gids - Configurator GIDs to fetch
   * @returns {Promise<Object>} - Map of GID to configurator data
   */
  async _fetchConfiguratorsViaGraphQL(gids) {
    // Get locale and translations from metadata
    const metadataElement = document.getElementById('VariantConfiguratorsMetadata');
    const metadata = metadataElement ? JSON.parse(metadataElement.textContent) : {};

    // Get locale from metadata with fallback
    let locale = metadata.locale || 'en-US';

    // Fallback to HTML lang attribute if metadata doesn't have locale
    if (!metadata.locale && document.documentElement.lang) {
      locale = document.documentElement.lang;
    }

    const translations = metadata.sku_translations || {};
    const lang = locale.split('-')[0].toUpperCase();

    const results = {};

    // ?_skuCache=ssr forces SSR-only mode for QA (errors if materialized data missing)
    const urlParams = new URLSearchParams(window.location.search);
    const forceSSR = urlParams.get('_skuCache') === 'ssr';

    // Try materialized data first (SSR-rendered, zero network cost)
    // Liquid outputs only the current language's data, keyed by GID.
    const materializedEl = document.getElementById('MaterializedConfigurators');
    if (materializedEl) {
      try {
        const materialized = JSON.parse(materializedEl.textContent);
        let allFound = true;
        const missing = [];
        for (const gid of gids) {
          const entry = materialized[gid];
          // Validate essential structure — must have size_options with at least one node
          if (entry && entry.size_options?.references?.nodes?.length > 0) {
            results[gid] = entry;
          } else {
            allFound = false;
            missing.push(gid);
          }
        }
        if (allFound) {
          console.log('[SkuPicker] Using materialized configurator data (SSR)');
          return results;
        }
        if (forceSSR) {
          throw new Error(`[SSR QA] Materialized data incomplete — missing GIDs: ${missing.join(', ')}`);
        }
        // Some GIDs missing or invalid — fall through to prefetch/fetch
        // Clear any partially-loaded results so the full set is fetched fresh
        for (const gid of gids) delete results[gid];
      } catch (err) {
        if (forceSSR) throw err;
        console.warn('[SkuPicker] Failed to parse materialized data, falling back to fetch:', err);
      }
    } else if (forceSSR) {
      throw new Error('[SSR QA] MaterializedConfigurators element not found — materialized fields not populated on configurator metaobjects');
    }

    const token = this._getStorefrontAccessToken();
    if (!token && !window.__skuPickerPrefetch) {
      throw new Error('No Storefront API access token found and no prefetch available');
    }

    // Try to use prefetched data (covers ALL GIDs from the Liquid prefetch script)
    if (window.__skuPickerPrefetch && window.__skuPickerPrefetchGids) {
      const prefetchGids = window.__skuPickerPrefetchGids;
      const allCovered = gids.every(gid => prefetchGids.includes(gid));
      if (allCovered) {
        try {
          const prefetchPromise = window.__skuPickerPrefetch;
          delete window.__skuPickerPrefetch;
          delete window.__skuPickerPrefetchGids;

          const response = await prefetchPromise;
          if (response.ok) {
            const result = await response.json();
            if (result.data) {
              gids.forEach(gid => {
                const prefetchIndex = prefetchGids.indexOf(gid);
                const configData = result.data[`config${prefetchIndex}`];
                if (configData) results[gid] = configData;
              });
              if (Object.keys(results).length === gids.length) {
                return results;
              }
              // Some GIDs missing from prefetch — fetch the rest below
            }
          }
        } catch (err) {
          console.warn('[SkuPicker] Prefetch failed, falling back to regular fetch:', err);
        }
      }
    }

    // Fetch any remaining configurators — try Railway cache first, then direct Storefront API
    const missingGids = gids.filter(gid => !results[gid]);
    if (missingGids.length > 0) {
      let fetched = false;
      // Try Railway cache (same endpoint the prefetch uses)
      try {
        const cacheController = new AbortController();
        const cacheTimeout = setTimeout(() => cacheController.abort(), 3000);
        const cacheUrl = `https://shop-chat-agent.iamfy.tech/api/sku-config?gids=${encodeURIComponent(missingGids.join(','))}&lang=${lang}`;
        const cacheRes = await fetch(cacheUrl, { signal: cacheController.signal });
        clearTimeout(cacheTimeout);
        if (cacheRes.ok) {
          const cacheData = await cacheRes.json();
          if (cacheData.data) {
            missingGids.forEach((gid, i) => {
              const entry = cacheData.data[`config${i}`];
              if (entry) results[gid] = entry;
            });
            if (missingGids.every(gid => results[gid])) fetched = true;
          }
        }
      } catch (cacheErr) {
        console.warn('[SkuPicker] Cache fallback failed, trying Storefront API:', cacheErr.message || cacheErr);
      }
      // Fall back to direct Storefront API batches
      if (!fetched) {
        const stillMissing = missingGids.filter(gid => !results[gid]);
        if (stillMissing.length > 0) {
          const batchSize = 5;
          const batchPromises = [];
          for (let i = 0; i < stillMissing.length; i += batchSize) {
            const batch = stillMissing.slice(i, i + batchSize);
            batchPromises.push(this._fetchConfiguratorBatch(batch, token, locale, translations));
          }
          const batchResultsArray = await Promise.all(batchPromises);
          batchResultsArray.forEach(br => Object.assign(results, br));
        }
      }
    }

    return results;
  }

  /**
   * Fetch a batch of configurators
   */
  static GID_PATTERN = /^gid:\/\/shopify\/Metaobject\/\d+$/;

  async _fetchConfiguratorBatch(gids, token, locale, translations) {
    // Validate GIDs to prevent GraphQL injection
    const validGids = gids.filter(gid => SkuPicker.GID_PATTERN.test(gid));
    if (validGids.length !== gids.length) {
      console.warn('[SkuPicker] Filtered invalid GIDs:', gids.filter(gid => !SkuPicker.GID_PATTERN.test(gid)));
    }

    // Build GraphQL query for this batch - request each field individually
    const queries = validGids.map((gid, index) => `
      config${index}: metaobject(id: "${gid}") {
        handle
        default_size_option: field(key: "default_size_option") {
          reference {
            ... on Metaobject {
              handle
            }
          }
        }
        default_framing_option: field(key: "default_framing_option") {
          reference {
            ... on Metaobject {
              handle
            }
          }
        }
        default_border_option: field(key: "default_border_option") {
          reference {
            ... on Metaobject {
              handle
            }
          }
        }
        treatment_default_size_option: field(key: "treatment_default_size_option") {
          reference {
            ... on Metaobject {
              handle
            }
          }
        }
        treatment_default_framing_option: field(key: "treatment_default_framing_option") {
          reference {
            ... on Metaobject {
              handle
            }
          }
        }
        framing_options_available: field(key: "framing_options_available") { value }

        size_title: field(key: "size_title") { value }
        size_guide_title: field(key: "size_guide_title") { value }
        size_blog_post: field(key: "size_blog_post") {
          value
          type
          key
        }
        size_blog_post_inches: field(key: "size_blog_post_inches") {
          value
          type
          key
        }

        framing_title: field(key: "framing_title") { value }
        framing_guide_title: field(key: "framing_guide_title") { value }
        framing_blog_post: field(key: "framing_blog_post") {
          value
          type
          key
        }

        border_title: field(key: "border_title") { value }
        border_guide_title: field(key: "border_guide_title") { value }
        border_blog_post: field(key: "border_blog_post") {
          value
          type
          key
        }

        size_options: field(key: "size_options") {
          references(first: 100) {
            nodes {
              ... on Metaobject {
                handle
                name: field(key: "name") { value }
                width_inches: field(key: "width_inches") { value }
                height_inches: field(key: "height_inches") { value }
                width_cms: field(key: "width_cms") { value }
                height_cms: field(key: "height_cms") { value }
              }
            }
          }
        }

        framing_options: field(key: "framing_options") {
          references(first: 100) {
            nodes {
              ... on Metaobject {
                handle
                name: field(key: "name") { value }
                short_name: field(key: "short_name") { value }
                description: field(key: "description") { value }
                is_new: field(key: "is_new") { value }
                frame_colors: field(key: "frame_colors") {
                  references(first: 50) {
                    nodes {
                      ... on Metaobject {
                        handle
                        name: field(key: "name") { value }
                        short_name: field(key: "short_name") { value }
                        hex_value: field(key: "hex_value") { value }
                        thumbnail: field(key: "thumbnail") {
                          reference {
                            ... on MediaImage {
                              image {
                                url
                              }
                            }
                          }
                        }
                        moulding_tile: field(key: "moulding_tile") {
                          reference {
                            ... on MediaImage {
                              image {
                                url
                              }
                            }
                          }
                        }
                        moulding_width: field(key: "moulding_width") { value }
                        prodigi_color: field(key: "prodigi_color") { value }
                      }
                    }
                  }
                }
              }
            }
          }
        }

        border_options: field(key: "border_options") {
          references(first: 100) {
            nodes {
              ... on Metaobject {
                handle
                name: field(key: "name") { value }
                thumbnail: field(key: "thumbnail") {
                  reference {
                    ... on MediaImage {
                      image {
                        url
                      }
                    }
                  }
                }
                is_new: field(key: "is_new") { value }
              }
            }
          }
        }

        size_pricing: field(key: "size_pricing") { value }
        framing_pricing: field(key: "framing_pricing") { value }
        border_pricing: field(key: "border_pricing") { value }
      }
    `).join('\n');

    const query = `query($language: LanguageCode) @inContext(language: $language) {
      ${queries}
    }`;

    const response = await fetch('/api/2025-07/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({
        query,
        variables: {
          language: locale.split('-')[0].toUpperCase() // "de-DE" → "DE"
        }
      }),
    });

    if (!response.ok) {
      throw new Error(`GraphQL request failed: ${response.status}`);
    }

    const result = await response.json();

    // Check for errors
    if (result.errors) {
      console.error('[SkuPicker] GraphQL errors:', result.errors);
      throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
    }

    const data = result.data;
    if (!data) {
      throw new Error('No data in GraphQL response');
    }

    // Map results back to GIDs
    const results = {};
    gids.forEach((gid, index) => {
      const configurator = data[`config${index}`];
      if (configurator) {
        results[gid] = configurator;
      } else {
        console.warn(`[SkuPicker] No configurator found for GID: ${gid}`);
      }
    });

    return results;
  }

  /**
   * Get translated string with fallback chain
   * @param {Object} translations - Full translations object
   * @param {string} locale - Current locale (e.g., "de-DE")
   * @param {string} category - Translation category (e.g., "size_options")
   * @param {string} key - Translation key (typically metaobject handle)
   * @param {string} fallbackValue - Original value to use if no translation found
   * @returns {string} Translated string or fallback
   */
  _getTranslation(translations, locale, category, key, fallbackValue) {
    if (!translations || !locale || !category || !key) {
      return fallbackValue;
    }

    const languageCode = locale.split('-')[0]; // "de-DE" → "de"

    // 1. Try full locale (e.g., "de-DE")
    if (translations[locale]?.[category]?.[key]) {
      return translations[locale][category][key];
    }

    // 2. Try language code (e.g., "de")
    if (translations[languageCode]?.[category]?.[key]) {
      return translations[languageCode][category][key];
    }

    // 3. Try en-US
    if (translations['en-US']?.[category]?.[key]) {
      return translations['en-US'][category][key];
    }

    // 4. Try en
    if (translations['en']?.[category]?.[key]) {
      return translations['en'][category][key];
    }

    // 5. Return original value
    return fallbackValue;
  }

  /**
   * Get translated UI string (pre-translated by Liquid via locale files)
   * @param {string} key - Translation key
   * @param {string} fallbackValue - Default English string
   * @returns {string} Translated string or fallback
   */
  _t(key, fallbackValue) {
    // Use pre-translated strings from Liquid locale files
    return this.uiStrings?.[key] || fallbackValue;
  }

  /**
   * Build variant configurator data from GraphQL response
   */
  _buildVariantConfiguratorData(configurator, marketHandle, locale, translations) {
    // Extract pricing data
    const sizePricing = this._extractPricingData(configurator.size_pricing, marketHandle);
    const framingPricing = this._extractPricingData(configurator.framing_pricing, marketHandle);
    const borderPricing = this._extractPricingData(configurator.border_pricing, marketHandle);

    // Extract option arrays with translations
    let sizeOptions = this._extractSizeOptions(configurator.size_options, locale, translations);
    const framingOptions = this._extractFramingOptions(configurator.framing_options, locale, translations);
    const borderOptions = this._extractBorderOptions(configurator.border_options, locale, translations);

    // Get default size
    let defaultSizeOption = configurator.default_size_option?.reference?.handle || '';

    // // Filter out 11x14 when croppable area is insufficient (< 5%)
    // if (this.croppableRatio < 0.05) {
    //   sizeOptions = sizeOptions.filter(opt => opt.handle !== '11-x-14');
    //   // If the default was 11x14, fall back to the first remaining size
    //   if (defaultSizeOption === '11-x-14' && sizeOptions.length > 0) {
    //     defaultSizeOption = sizeOptions[0].handle;
    //   }
    // }

    return {
      defaults: {
        size_option: defaultSizeOption,
        framing_option: configurator.default_framing_option?.reference?.handle || '',
        border_option: configurator.default_border_option?.reference?.handle || ''
      },
      treatmentDefaults: {
        size_option: configurator.treatment_default_size_option?.reference?.handle || '',
        framing_option: configurator.treatment_default_framing_option?.reference?.handle || ''
      },
      framing_options_available: configurator.framing_options_available?.value || 0,
      options: {
        size: sizeOptions,
        framing: framingOptions,
        border: borderOptions
      },
      size_pricing: sizePricing,
      framing_pricing: framingPricing,
      border_pricing: borderPricing,
      guides: {
        size: {
          section_title: this._getTranslation(translations, locale, 'configurator', 'size_title', configurator.size_title?.value || ''),
          guide_title: this._getTranslation(translations, locale, 'configurator', 'size_guide_title', configurator.size_guide_title?.value || ''),
          content: '' // Will be populated by _fetchArticleContent
        },
        size_inches: {
          section_title: this._getTranslation(translations, locale, 'configurator', 'size_title', configurator.size_title?.value || ''),
          guide_title: this._getTranslation(translations, locale, 'configurator', 'size_guide_title', configurator.size_guide_title?.value || ''),
          content: '' // Will be populated by _fetchArticleContent
        },
        framing: {
          section_title: this._getTranslation(translations, locale, 'configurator', 'framing_title', configurator.framing_title?.value || ''),
          guide_title: this._getTranslation(translations, locale, 'configurator', 'framing_guide_title', configurator.framing_guide_title?.value || ''),
          content: '' // Will be populated by _fetchArticleContent
        },
        border: {
          section_title: this._getTranslation(translations, locale, 'configurator', 'border_title', configurator.border_title?.value || ''),
          guide_title: this._getTranslation(translations, locale, 'configurator', 'border_guide_title', configurator.border_guide_title?.value || ''),
          content: '' // Will be populated by _fetchArticleContent
        }
      },
      // Store raw article references temporarily for content fetching
      _rawGuideRefs: {
        size: configurator.size_blog_post,
        size_inches: configurator.size_blog_post_inches,
        framing: configurator.framing_blog_post,
        border: configurator.border_blog_post
      }
    };
  }

  /**
   * Fetch article content for guide fields via separate GraphQL queries
   * @param {Object} variants - The variants object with configurator data
   */
  async _fetchArticleContent(variants) {
    // Collect all unique article IDs that need to be fetched
    const articleIds = new Set();
    const articleMap = {}; // Map to track which guides need which articles

    for (const [variantId, config] of Object.entries(variants)) {
      ['size', 'size_inches', 'framing', 'border'].forEach(guideType => {
        const blogPostRef = config._rawGuideRefs?.[guideType];
        // The value field contains the article GID (e.g., "gid://shopify/OnlineStoreArticle/123")
        if (blogPostRef?.value && typeof blogPostRef.value === 'string' && blogPostRef.value.trim() !== '') {
          let articleId = blogPostRef.value.trim();

          // Convert OnlineStoreArticle GID to Article GID for Storefront API
          if (articleId.startsWith('gid://shopify/OnlineStoreArticle/')) {
            articleId = articleId.replace('gid://shopify/OnlineStoreArticle/', 'gid://shopify/Article/');
          }

          // Validate that it looks like a valid Article GID
          if (articleId.startsWith('gid://shopify/Article/')) {
            articleIds.add(articleId);

            if (!articleMap[articleId]) {
              articleMap[articleId] = [];
            }
            articleMap[articleId].push({ variantId, guideType });
          }
        }
      });
    }

    if (articleIds.size === 0) {
      return;
    }

    // Fetch all articles in a single batch query
    const token = this._getStorefrontAccessToken();
    const articleQueries = Array.from(articleIds).map((id, index) => {
      // Use article(id:) query with the GID from the metafield value
      return `article${index}: article(id: "${id}") {
        id
        title
        contentHtml
      }`;
    });

    const query = `query { ${articleQueries.join('\n')} }`;

    try {
      const response = await fetch('/api/2025-07/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query }),
      });

      if (!response.ok) {
        console.warn('[SkuPicker] Failed to fetch article content:', response.status);
        return;
      }

      const result = await response.json();

      if (result.errors) {
        console.warn('[SkuPicker] GraphQL errors fetching articles:', result.errors);
        return;
      }

      // Map article content back to guide fields
      const articles = result.data;
      const idToIndex = {};
      Array.from(articleIds).forEach((id, index) => {
        idToIndex[id] = `article${index}`;
      });

      for (const [articleId, locations] of Object.entries(articleMap)) {
        const articleKey = idToIndex[articleId];
        const article = articles[articleKey];

        if (article?.contentHtml) {
          locations.forEach(({ variantId, guideType }) => {
            variants[variantId].guides[guideType].content = article.contentHtml;
          });
        }
      }
    } catch (error) {
      console.error('[SkuPicker] Error fetching article content:', error);
    }
  }

  /**
   * Background-load article content after picker is interactive.
   * Populates guide content into already-initialized variant data.
   */
  _backgroundLoadArticleContent() {
    if (!this.variantConfigurators?.variants) return;
    this._articleContentPromise = this._fetchArticleContent(this.variantConfigurators.variants)
      .then(() => {
        // Clean up _rawGuideRefs after successful fetch
        for (const config of Object.values(this.variantConfigurators.variants)) {
          delete config._rawGuideRefs;
        }
      })
      .catch(err => console.warn('[SkuPicker] Background article fetch failed:', err));
  }

  /**
   * Extract size options from GraphQL field
   */
  _extractSizeOptions(field, locale, translations) {
    if (!field || !field.references || !field.references.nodes) return [];

    return field.references.nodes.map(item => ({
      handle: item.handle,
      label: this._getTranslation(translations, locale, 'size_options', item.handle, item.name?.value || item.handle),
      width_inches: item.width_inches?.value || '',
      height_inches: item.height_inches?.value || '',
      width_cms: item.width_cms?.value || '',
      height_cms: item.height_cms?.value || ''
    }));
  }

  /**
   * Extract framing options from GraphQL field
   */
  _extractFramingOptions(field, locale, translations) {
    if (!field || !field.references || !field.references.nodes) return [];

    return field.references.nodes.map(item => {
      const colors = item.frame_colors?.references?.nodes?.map(color => ({
        handle: color.handle,
        label: this._getTranslation(translations, locale, 'frame_colors', color.handle, color.name?.value || color.handle),
        short_name: this._getTranslation(translations, locale, 'frame_colors', `${color.handle}_short`, color.short_name?.value || color.name?.value || ''),
        color_value: color.hex_value?.value || '',
        thumbnail: color.thumbnail?.reference?.image?.url || '',
        moulding_tile: color.moulding_tile?.reference?.image?.url || '',
        // moulding_width drives the 2D frame-mock band width. The framing
        // option's profile_width_mm is the canonical frame width — it scales
        // the 3D profile_points — so prefer it here to keep the 2D mock and
        // the 3D preview the same thickness. Falls back to the per-color value.
        moulding_width: item.profile_width_mm?.value != null
          ? Number(item.profile_width_mm.value)
          : (color.moulding_width?.value ? Number(color.moulding_width.value) : null),
        prodigi_color: color.prodigi_color?.value || '',
        metalness: color.metalness?.value != null ? Number(color.metalness.value) : null,
        roughness: color.roughness?.value != null ? Number(color.roughness.value) : null,
        outer_face_material: color.outer_face_material?.value || null,
        inner_face_material: color.inner_face_material?.value || null,
      })) || [];

      return {
        handle: item.handle,
        label: this._getTranslation(translations, locale, 'framing_options', item.handle, item.name?.value || item.handle),
        short_name: this._getTranslation(translations, locale, 'framing_options', `${item.handle}_short`, item.short_name?.value || item.name?.value || ''),
        description: this._getTranslation(translations, locale, 'framing_options', `${item.handle}_desc`, item.description?.value || ''),
        is_new: item.is_new?.value === 'true',
        profile_points: item.profile_points?.value || null,
        lip_depth_normalized: item.lip_depth_normalized?.value != null ? Number(item.lip_depth_normalized.value) : null,
        profile_width_mm: item.profile_width_mm?.value != null ? Number(item.profile_width_mm.value) : null,
        profile_depth_mm: item.profile_depth_mm?.value != null ? Number(item.profile_depth_mm.value) : null,
        colors: colors
      };
    });
  }

  /**
   * Extract border options from GraphQL field
   */
  _extractBorderOptions(field, locale, translations) {
    if (!field || !field.references || !field.references.nodes) return [];

    return field.references.nodes.map(item => ({
      handle: item.handle,
      label: this._getTranslation(translations, locale, 'border_options', item.handle, item.name?.value || item.handle),
      thumbnail: item.thumbnail?.reference?.image?.url || '',
      is_new: item.is_new?.value === 'true'
    }));
  }

  /**
   * Extract pricing data from JSON field
   * Only includes options that have prices defined for the current market
   */
  _extractPricingData(field, marketHandle) {
    if (!field || !field.value) return [];

    try {
      const pricingData = JSON.parse(field.value);
      if (!Array.isArray(pricingData)) return [];

      return pricingData
        .map(item => {
          // Get price for current market (in major units like 25.00)
          // Do NOT fall back to default - only use market-specific price
          const priceMarket = item.price?.[marketHandle];
          const compareAtMarket = item.compare_at_price?.[marketHandle];

          // If no price exists for this market, mark as unavailable
          if (priceMarket === undefined || priceMarket === null) {
            return null;
          }

          // Convert to cents
          const priceCents = Math.round(parseFloat(priceMarket) * 100);
          const compareAtCents = compareAtMarket !== undefined && compareAtMarket !== null
            ? Math.round(parseFloat(compareAtMarket) * 100)
            : 0;

          return {
            size_option: item.size_option || '',
            framing_option: item.framing_option || '',
            border_option: item.border_option || '',
            price: priceCents,
            compare_at_price: compareAtCents
          };
        })
        .filter(item => item !== null); // Remove unavailable options
    } catch (error) {
      console.error('[SkuPicker] Failed to parse pricing JSON:', error);
      return [];
    }
  }

  /**
   * Get Storefront API access token
   */
  _getStorefrontAccessToken() {
    // Check for token in meta tag
    const metaTag = document.querySelector('meta[name="shopify-storefront-api-token"]');
    if (metaTag?.content) {
      return metaTag.content;
    }

    // Check for token in data attribute
    const tokenElement = document.querySelector('[data-storefront-access-token]');
    if (tokenElement?.dataset.storefrontAccessToken) {
      return tokenElement.dataset.storefrontAccessToken;
    }

    // Check in window.Shopify global
    if (window.Shopify?.storefrontAccessToken) {
      return window.Shopify.storefrontAccessToken;
    }

    return null;
  }

  /**
   * Initialize unit preference from localStorage or default. The surgical
   * PDP writes the user's choice to `pdp_unit`; honour that first so the
   * Size property attached on ATC matches what the buyer saw on the PDP.
   */
  _initializeUnitPreference() {
    const pdpUnit = localStorage.getItem('pdp_unit');
    const legacyUnit = localStorage.getItem('skuPicker_unitPreference');
    const fromStore =
      (pdpUnit === 'in' || pdpUnit === 'cm') ? pdpUnit :
      (legacyUnit === 'in' || legacyUnit === 'cm') ? legacyUnit :
      null;
    if (fromStore) {
      this.currentUnit = fromStore;
    } else {
      this.currentUnit = this.variantConfigurators.defaultUnit || 'in';
    }
  }

  /**
   * Setup event delegation on the container (called once during initialization).
   * This replaces per-render event listener attachment for better INP performance.
   */
  _setupEventDelegation() {
    const signal = this._abortController.signal;
    this.container.addEventListener('click', (e) => {
      // Handle .remove-frame-btn clicks (check BEFORE .option-main since it's nested inside)
      const removeFrameBtn = e.target.closest('.remove-frame-btn');
      if (removeFrameBtn) {
        e.stopPropagation();
        this.state.confirmed.framing_option = 'unframed';
        this.state.confirmed.frame_color = null;
        // Reset border to 'no-border' if it's currently 'mount' (mount requires frame)
        if (this.state.confirmed.border_option === 'mount') {
          this.state.confirmed.border_option = 'no-border';
        }
        this._updateConfirmedSelection(true); // User-initiated removal of frame
        this._renderOptionRows();
        return;
      }

      // Handle .info-link clicks (check BEFORE .option-main since it may be nested)
      const infoLink = e.target.closest('.info-link');
      if (infoLink) {
        e.preventDefault();
        const guideType = infoLink.dataset.guideFor;
        if (guideType) {
          this._openGuideModal(guideType);
        }
        return;
      }

      // Handle .option-main clicks (open modal) - check last as it's the parent container
      const optionMain = e.target.closest('.option-main');
      if (optionMain) {
        // Don't open modal if the option is disabled
        if (optionMain.classList.contains('disabled')) return;
        const optionName = optionMain.dataset.optionName;
        if (optionName) {
          this._openModal(optionName);
        }
        return;
      }
    }, { signal });

    // Listen for size impact slider changes (bubbles from <size-impact-slider>)
    this.container.addEventListener('size-impact:changed', (e) => {
      if (this._statementPairActive) return; // Guard: ignore size changes in SP mode
      const { handle } = e.detail || {};
      if (!handle || handle === this.state.confirmed.size_option) return;
      this.state.confirmed.size_option = handle;
      this._updateConfirmedSelection(true);
    }, { signal });

    // Listen for Statement Pair mode toggle
    document.addEventListener('statement-pair:toggled', (e) => {
      this._handleStatementPairMode(e.detail?.active);
    }, { signal });
  }

  /**
   * Handle Statement Pair mode toggle — swap add-to-cart for "Add to wall" button
   */
  _handleStatementPairMode(active) {
    this._statementPairActive = !!active;

    const form = this.container.closest('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    const addToCartButton = form.querySelector('[name="add"]');
    if (!addToCartButton) return;

    if (active) {
      // Hide add-to-cart, show "Add to wall"
      addToCartButton.style.display = 'none';

      let addToWallBtn = form.querySelector('.statement-pair-add-to-wall');
      if (!addToWallBtn) {
        // Get product handle from room mock or meta tag
        const roomMock = document.querySelector('room-background-mock[data-product-handle]');
        const productHandle = roomMock?.dataset.productHandle || '';

        addToWallBtn = document.createElement('a');
        addToWallBtn.className = 'statement-pair-add-to-wall button button--primary';
        addToWallBtn.href = `/a/gallery-wall/build/diptych?artworks=${encodeURIComponent(productHandle)}`;
        addToWallBtn.textContent = 'Add to wall';
        addToWallBtn.style.cssText = 'display:flex;align-items:center;justify-content:center;width:100%;text-decoration:none;';
        addToCartButton.parentNode.insertBefore(addToWallBtn, addToCartButton.nextSibling);
      }

      addToWallBtn.style.display = 'flex';
    } else {
      // Restore add-to-cart, hide "Add to wall"
      addToCartButton.style.display = '';
      const addToWallBtn = form.querySelector('.statement-pair-add-to-wall');
      if (addToWallBtn) addToWallBtn.style.display = 'none';
    }
  }

  /**
   * Set unit preference and save to localStorage
   */
  _setUnitPreference(unit) {
    if (unit !== 'in' && unit !== 'cm') return;
    this.currentUnit = unit;
    localStorage.setItem('skuPicker_unitPreference', unit);
    document.dispatchEvent(new CustomEvent('sku-picker:unit-changed', {
      detail: { unit },
    }));
  }

  /**
   * Format dimensions in inches: 9" × 12"
   */
  _formatInches(width, height) {
    if (!width || !height) return '';
    // Remove trailing zeros and decimal point if not needed
    const w = parseFloat(width).toString();
    const h = parseFloat(height).toString();
    return `${w}" × ${h}"`;
  }

  /**
   * Format dimensions in centimeters: 30 × 40.5cm
   */
  _formatCm(width, height) {
    if (!width || !height) return '';
    // Remove trailing zeros and decimal point if not needed
    const w = parseFloat(width).toString();
    const h = parseFloat(height).toString();
    return `${w} × ${h}cm`;
  }

  /**
   * Format size dimensions based on current unit preference
   */
  _formatSizeDimensions(sizeOption) {
    if (this.currentUnit === 'in') {
      return this._formatInches(sizeOption.width_inches, sizeOption.height_inches);
    } else {
      return this._formatCm(sizeOption.width_cms, sizeOption.height_cms);
    }
  }

  _getSizeBucketName(sizeOption) {
    const buckets = [
      { label: 'Nook', maxInches: 12 },
      { label: 'Accent', maxInches: 16 },
      { label: 'Focal Point', maxInches: 32 },
      { label: 'Statement', maxInches: Infinity },
    ];
    const maxDim = Math.max(
      parseFloat(sizeOption.width_inches) || 0,
      parseFloat(sizeOption.height_inches) || 0
    );
    return (buckets.find(b => maxDim <= b.maxInches) || buckets[buckets.length - 1]).label;
  }

  /**
   * Create unit toggle component (cm | in)
   */
  _createUnitToggle() {
    const toggle = document.createElement('span');
    toggle.className = 'unit-toggle';
    toggle.innerHTML = `<button type="button" class="unit-option ${this.currentUnit === 'cm' ? 'active' : ''}" data-unit="cm">cm</button><span class="separator">|</span><button type="button" class="unit-option ${this.currentUnit === 'in' ? 'active' : ''}" data-unit="in">in</button>`;

    toggle.querySelectorAll('.unit-option').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const newUnit = btn.dataset.unit;
        if (newUnit !== this.currentUnit) {
          this._setUnitPreference(newUnit);
          this._renderOptionRows();
        }
      });
    });

    return toggle;
  }

  /**
   * Get price value - now simplified since prices are already converted in Liquid
   */
  _getPrice(priceValue) {
    if (!priceValue && priceValue !== 0) return 0;
    const price = parseFloat(priceValue);
    return isNaN(price) ? 0 : price;
  }

  /**
   * Format price using Shopify money format
   */
  _formatPrice(priceInCents) {
    if (typeof priceInCents !== 'number') return '';
    try {
      const amount = (priceInCents / 100).toFixed(2);
      return this.moneyFormat.replace(/{{\s*amount.*?\s*}}/g, amount);
    } catch (e) {
      return `${(priceInCents / 100).toFixed(2)}`;
    }
  }

  _preprocessData() {
    if (!this.data) {
      return;
    }

    // Reset components
    this.components = { size: {}, framing: {}, border: {} };

    // Process size pricing (prices are already converted to user's currency in Liquid)
    if (this.data.size_pricing && Array.isArray(this.data.size_pricing)) {
      this.data.size_pricing.forEach(item => {
        if (!item.size_option) return; // Skip invalid entries

        const price = this._getPrice(item.price);
        const compareAtPrice = this._getPrice(item.compare_at_price);

        this.components.size[item.size_option] = {
          price: price,
          price_formatted: this._formatPrice(price),
          compare_at_price: compareAtPrice,
          compare_at_price_formatted: compareAtPrice > 0 ? this._formatPrice(compareAtPrice) : null
        };
      });
    }

    // Process framing pricing (prices are already converted to user's currency in Liquid)
    if (this.data.framing_pricing && Array.isArray(this.data.framing_pricing)) {
      this.data.framing_pricing.forEach(item => {
        if (!item.size_option || !item.framing_option) return; // Skip invalid entries

        const price = this._getPrice(item.price);
        const compareAtPrice = this._getPrice(item.compare_at_price);

        if (!this.components.framing[item.size_option]) {
          this.components.framing[item.size_option] = {};
        }

        this.components.framing[item.size_option][item.framing_option] = {
          price: price,
          price_formatted: this._formatPrice(price),
          compare_at_price: compareAtPrice,
          compare_at_price_formatted: compareAtPrice > 0 ? this._formatPrice(compareAtPrice) : null
        };
      });
    }

    // Process border pricing (prices are already converted to user's currency in Liquid)
    const NO_MOUNT_SIZES = new Set(['11-x-14', '14-x-11']);
    if (this.data.border_pricing && Array.isArray(this.data.border_pricing)) {
      this.data.border_pricing.forEach(item => {
        if (!item.size_option || !item.border_option) return; // Skip invalid entries
        // 11×14 / 14×11 are only available unframed — suppress the mount option
        if (NO_MOUNT_SIZES.has(item.size_option) && item.border_option === 'mount') return;

        const price = this._getPrice(item.price);
        const compareAtPrice = this._getPrice(item.compare_at_price);

        if (!this.components.border[item.size_option]) {
          this.components.border[item.size_option] = {};
        }

        this.components.border[item.size_option][item.border_option] = {
          price: price,
          price_formatted: this._formatPrice(price),
          compare_at_price: compareAtPrice,
          compare_at_price_formatted: compareAtPrice > 0 ? this._formatPrice(compareAtPrice) : null
        };
      });
    }
  }

  _setInitialState() {
    // Start with defaults
    this.state.confirmed = { ...this.data.defaults };
    const defaultFrame = this.data.options.framing.find(f => f.handle === this.data.defaults.framing_option);
    if (defaultFrame && defaultFrame.colors.length > 0) { this.state.confirmed.frame_color = defaultFrame.colors[0].handle; }
    else { this.state.confirmed.frame_color = null; }

    // Try to restore state from URL params
    const urlState = this._getStateFromURL();
    const hasUrlState = urlState && Object.keys(urlState).length > 0;
    if (hasUrlState) {
      // Merge URL state with defaults (URL params override defaults)
      Object.assign(this.state.confirmed, urlState);
    }

    // When style-picker is enabled, defer URL updates, mock events, and price
    // display to it. The style picker will apply its active preset shortly
    // after init, which sets the correct framing/border/color, dispatches
    // config-changed, and updates the price.
    const hasStylePicker = this.container.hasAttribute('data-has-style-picker');
    if (hasStylePicker && !hasUrlState) {
      // When "Open at lowest price" is on, leave the state at the plain
      // configurator defaults (smallest size, unframed, no-border) so the PDP
      // opens at the lowest price: style-picker's #detectActivePreset then
      // matches the unframed preset (always present, position 4) instead of a
      // framed preset. When off, seed the per-configurator treatment defaults
      // (a larger size + a framed option) below.
      const lowestPriceDefault = this.container.hasAttribute('data-lowest-price-default');
      if (!lowestPriceDefault) {
        // Mirrored at SSR by the LCP default-mock resolver in blocks/_product-media-gallery.liquid — keep in sync.
        // Apply treatment-specific defaults (stored per-configurator in metaobjects)
        const td = this.data.treatmentDefaults;
        if (td?.size_option && this.data.options.size?.some(s => s.handle === td.size_option)) {
          this.state.confirmed.size_option = td.size_option;
        }
        if (td?.framing_option && this.data.options.framing?.some(f => f.handle === td.framing_option)) {
          this.state.confirmed.framing_option = td.framing_option;
          // Update frame_color and border to match the treatment framing.
          // They were set from the regular defaults and may not correspond,
          // causing style-picker's #detectActivePreset to fail its 4-field
          // match and fall back to presets[0] — discarding treatment defaults.
          const treatmentFrame = this.data.options.framing.find(f => f.handle === td.framing_option);
          this.state.confirmed.frame_color = treatmentFrame?.colors?.[0]?.handle || null;
          // Mount requires a frame; reset to no-border if treatment is unframed
          if (td.framing_option === 'unframed' && this.state.confirmed.border_option === 'mount') {
            this.state.confirmed.border_option = 'no-border';
          }
        }
      }

      // Compute internals and write form properties with initial defaults.
      // Style-picker will overwrite these with the correct preset config once
      // its async init completes, but the form must never be empty — users
      // may add to cart before style-picker finishes loading presets.
      this.currentSelectionDetails = this._getSelectionDetails(this.state.confirmed);
      this._updateFormProperties();
      return;
    }

    // Update confirmed selection - treat URL state as user-initiated so mocks update
    this._updateConfirmedSelection(hasUrlState);

  }

  /**
   * Reads SKU picker state from URL query parameters
   * Returns validated state object or null if no valid params found
   */
  _getStateFromURL() {
    const params = new URLSearchParams(window.location.search);
    // Fallback: pdp-ab-test.js snapshots inbound params before clearing
    // the URL, so the new sku-picker can still restore the cart selection.
    const inbound = window.__pdpInboundSkuParams;
    const state = {};

    // Read and validate each parameter (URL first, then inbound snapshot)
    const sizeParam = params.get('size') || inbound?.size || null;
    const framingParam = params.get('framing') || inbound?.framing || null;
    const borderParam = params.get('border') || inbound?.border || null;
    const frameColorParam = params.get('frame-color') || inbound?.['frame-color'] || null;

    // Validate size option exists
    if (sizeParam) {
      const sizeExists = this.data.options.size?.some(s => s.handle === sizeParam);
      if (sizeExists) {
        state.size_option = sizeParam;
      }
    }

    // Validate framing option exists
    if (framingParam) {
      const framingExists = this.data.options.framing?.some(f => f.handle === framingParam);
      if (framingExists) {
        state.framing_option = framingParam;
      }
    }

    // Validate border option exists
    if (borderParam) {
      const borderExists = this.data.options.border?.some(b => b.handle === borderParam);
      if (borderExists) {
        state.border_option = borderParam;
      }
    }

    // Validate frame color exists for the selected framing
    if (frameColorParam && state.framing_option) {
      const framingOption = this.data.options.framing?.find(f => f.handle === state.framing_option);
      const colorExists = framingOption?.colors?.some(c => c.handle === frameColorParam);
      if (colorExists) {
        state.frame_color = frameColorParam;
      }
    }

    // If no valid params found, return null
    if (Object.keys(state).length === 0) {
      return null;
    }

    // Build a test selection by merging with defaults
    const testSelection = {
      ...this.data.defaults,
      frame_color: null,
      ...state
    };

    // If framing is set but frame_color isn't, set default color for that frame
    if (testSelection.framing_option && testSelection.framing_option !== 'unframed' && !state.frame_color) {
      const framingOption = this.data.options.framing?.find(f => f.handle === testSelection.framing_option);
      if (framingOption?.colors?.length > 0) {
        testSelection.frame_color = framingOption.colors[0].handle;
        state.frame_color = testSelection.frame_color;
      }
    }

    // Check if the combination is available
    const details = this._getSelectionDetails(testSelection);
    if (!details.available) {
      // Combination not available, try to salvage what we can
      // Start with just size if it's valid on its own
      if (state.size_option) {
        const sizeOnlySelection = {
          ...this.data.defaults,
          size_option: state.size_option,
          frame_color: null
        };
        const defaultFrame = this.data.options.framing.find(f => f.handle === this.data.defaults.framing_option);
        if (defaultFrame?.colors?.length > 0) {
          sizeOnlySelection.frame_color = defaultFrame.colors[0].handle;
        }
        const sizeDetails = this._getSelectionDetails(sizeOnlySelection);
        if (sizeDetails.available) {
          return { size_option: state.size_option };
        }
      }
      return null;
    }

    return state;
  }

  /**
   * Updates URL query parameters with current SKU picker state
   * Uses replaceState to avoid polluting browser history
   */
  _updateURL() {
    const params = new URLSearchParams(window.location.search);
    const { size_option, framing_option, border_option, frame_color } = this.state.confirmed;

    // Update params
    if (size_option) params.set('size', size_option);
    if (framing_option) params.set('framing', framing_option);
    if (border_option) params.set('border', border_option);
    if (frame_color && framing_option !== 'unframed') {
      params.set('frame-color', frame_color);
    } else {
      params.delete('frame-color');
    }

    // Build new URL
    const newURL = `${window.location.pathname}?${params.toString()}${window.location.hash}`;

    // Update URL without adding history entry
    window.history.replaceState(null, '', newURL);
  }

  _formatTotalPrice(priceInCents) {
    // Format total price by using the money format template
    // This is only needed for the final total since individual prices come pre-formatted
    if (typeof priceInCents !== 'number') return '';
    try {
      const amount = (priceInCents / 100).toFixed(2);
      return this.moneyFormat.replace(/{{\s*amount.*?\s*}}/g, amount);
    } catch (e) {
      return `${(priceInCents / 100).toFixed(2)}`;
    }
  }

  /**
   * Detects the initial variant from product form (most reliable)
   */
  _detectInitialVariant() {
    // Try product form first (most reliable - reads current selection)
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        const variantId = String(variantInput.value);
        if (this.variantConfigurators.variants[variantId]) {
          this.currentVariantId = variantId;
          return;
        }
      }
    }

    // Try URL parameter as fallback
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get('variant');
    if (variantParam) {
      const variantId = String(variantParam);
      if (this.variantConfigurators.variants[variantId]) {
        this.currentVariantId = variantId;
        return;
      }
    }

    // No configurator found for current variant
    this.currentVariantId = null;
  }

  /**
   * Sets up listener for Shopify variant selector changes
   */
  _setupVariantListener() {
    // Find the shopify-section containing this SKU picker
    const section = this.container.closest('.shopify-section');
    const target = section || document;

    // Listen for the theme's variant:update event
    target.addEventListener('variant:update', (event) => {
      const newVariant = event.detail?.resource;

      if (newVariant && newVariant.id) {
        // Track variant availability
        this.currentVariantAvailable = newVariant.available !== false;
        this._handleVariantChange(String(newVariant.id));
      }
    }, { signal: this._abortController.signal });
  }

  /**
   * Sets loading state during variant transitions
   */
  _setLoadingState(isLoading) {
    if (isLoading) {
      this.container.style.opacity = '0.6';
      this.container.style.pointerEvents = 'none';
      this.container.setAttribute('aria-busy', 'true');
      // Announce loading to screen readers
      if (!this._liveRegion) {
        this._liveRegion = document.createElement('div');
        this._liveRegion.setAttribute('aria-live', 'polite');
        this._liveRegion.setAttribute('role', 'status');
        this._liveRegion.className = 'visually-hidden';
        this.container.appendChild(this._liveRegion);
      }
      this._liveRegion.textContent = 'Loading…';
    } else {
      this.container.style.opacity = '';
      this.container.style.pointerEvents = '';
      this.container.removeAttribute('aria-busy');
      if (this._liveRegion) this._liveRegion.textContent = '';
    }
  }

  /**
   * Handles variant change with debouncing for better performance
   */
  _handleVariantChange(newVariantId) {
    // Validate variant ID
    if (!newVariantId || newVariantId === 'undefined' || newVariantId === 'null') {
      return;
    }

    // Check if this variant has a configurator
    if (!this.variantConfigurators.variants[newVariantId]) {
      this.container.innerHTML = '<div style="padding: 20px; color: #999; text-align: center;">This variant does not have a configurator.</div>';
      this.container.style.display = '';
      this.currentVariantId = newVariantId;
      this.hasConfiguratorLoaded = false;
      return;
    }

    // Check if variant is different from current AND a configurator is already loaded
    if (newVariantId === this.currentVariantId && this.hasConfiguratorLoaded) {
      return; // No change needed
    }

    // Cancel any pending variant change
    if (this._variantChangeTimeout) {
      cancelAnimationFrame(this._variantChangeTimeout);
    }

    // Show loading state immediately for better perceived performance
    this._setLoadingState(true);

    // Debounce the actual variant loading to avoid rapid consecutive changes
    this._variantChangeTimeout = requestAnimationFrame(() => {
      this.currentVariantId = newVariantId;
      this._loadVariantConfigurator(newVariantId);

      // Emit variant changed event for gallery / quality chips / learn-more.
      // Detail includes `productType` so listeners don't have to query
      // `window.skuPickerInstance.variantConfigurators` to branch UI.
      const variantData = this.variantConfigurators.variants[newVariantId];
      const productType = variantData?.artProduct === 'canvas-print' ? 'canvas' : 'art_print';
      const event = new CustomEvent('sku-picker:variant-changed', {
        bubbles: true,
        detail: {
          variantId: newVariantId,
          productType
        }
      });
      this.container.dispatchEvent(event);

      // Dispatch product type change for frame/room mocks
      if (variantData && variantData.artProduct) {
        document.dispatchEvent(new CustomEvent('gallery-wall:config-changed', {
          bubbles: true,
          detail: { productType }
        }));
      }

      this._setLoadingState(false);
    });
  }

  /**
   * Loads configurator data for a specific variant
   */
  _loadVariantConfigurator(variantId) {
    const variantConfig = this.variantConfigurators.variants[variantId];
    if (!variantConfig) {
      this.container.innerHTML = '<div style="padding: 20px; color: #999;">This variant does not have a configurator.</div>';
      return;
    }

    // Set the data to the variant-specific configurator
    this.data = variantConfig;

    // Reset components
    this.components = { size: {}, framing: {}, border: {} };

    // Check if this is a variant switch (configurator was already loaded)
    const isVariantSwitch = this.hasConfiguratorLoaded;
    const prevSize = isVariantSwitch ? this.state.confirmed.size_option : null;

    // Reprocess data and set initial state
    this._preprocessData();
    this._setInitialState();

    // Check if SSR content is present (option-row elements already exist)
    const hasSSRContent = this.container.querySelectorAll('.option-row').length > 0;

    // Check if skeleton loader is present
    const hasSkeleton = this.container.querySelector('.sku-picker-skeleton');

    if (isVariantSwitch) {
      // Preserve current size across variant switch (slider uses union of all sizes),
      // but only if the previous size exists in the new variant's options.
      if (prevSize && this.data.options.size?.some(s => s.handle === prevSize)) {
        this.state.confirmed.size_option = prevSize;
      }
      // _setInitialState wrote state with the new variant's defaults before
      // prevSize was restored — recompute and rewrite form/URL with the
      // correct size.  When style-picker is active it owns the final
      // _updateConfirmedSelection call, so only update form properties here.
      this.currentSelectionDetails = this._getSelectionDetails(this.state.confirmed);
      if (this.container.hasAttribute('data-has-style-picker')) {
        this._updateFormProperties();
      } else {
        this._updateConfirmedSelection(false);
      }

      if (this.container.hasAttribute('data-has-style-picker')) {
        const existingSlider = this.container.querySelector('size-impact-slider');
        if (existingSlider) {
          existingSlider.setAttribute('data-default-size', this.state.confirmed.size_option || '');
        }
      } else {
        // Variant switch: rebuild non-slider rows, preserve the size slider to avoid
        // a visible reset flash (the slider uses a union of all variant sizes).
        const existingSlider = this.container.querySelector('size-impact-slider');
        if (existingSlider) {
          existingSlider.setAttribute('data-default-size', this.state.confirmed.size_option || '');
        }
        this.container.innerHTML = '';
        this._renderOptionRows({ reuseSlider: existingSlider });
      }
    } else if (hasSSRContent) {
      // Initial load with SSR content: enhance it
      this._enhanceSSRContent();
    } else {
      // Initial load without SSR content or with skeleton: render from scratch
      if (hasSkeleton) {
        // Remove skeleton before rendering actual content
        this.container.innerHTML = '';
      }
      this._renderOptionRows();
    }

    // Show container if it was hidden
    this.container.style.display = '';
    this.hasConfiguratorLoaded = true;
  }

  /**
   * Enhance SSR-rendered content with dynamic features
   */
  _enhanceSSRContent() {
    // Add unit toggle to size option row
    const sizeRow = this.container.querySelector('[data-option-name="size"]')?.closest('.option-row');
    if (sizeRow) {
      const optionLeft = sizeRow.querySelector('.option-left');
      if (optionLeft && !optionLeft.querySelector('.unit-toggle')) {
        const sizeLabel = optionLeft.querySelector('span');
        const unitToggle = this._createUnitToggle();

        // Insert toggle after the size label
        if (sizeLabel && sizeLabel.nextSibling) {
          optionLeft.insertBefore(unitToggle, sizeLabel.nextSibling);
        } else if (sizeLabel) {
          sizeLabel.after(unitToggle);
        }
      }
    }

    // Update size display with proper formatting
    this._updateSSRSizeDisplay();

    // Attach event listeners
    this._attachEventListeners();
  }

  /**
   * Update SSR-rendered size display with proper unit formatting
   */
  _updateSSRSizeDisplay() {
    const sizeOptionValue = this.container.querySelector('[data-option-name="size"] .option-value');
    if (sizeOptionValue) {
      const widthIn = sizeOptionValue.dataset.widthInches;
      const heightIn = sizeOptionValue.dataset.heightInches;
      const widthCm = sizeOptionValue.dataset.widthCms;
      const heightCm = sizeOptionValue.dataset.heightCms;

      if (widthIn && heightIn && widthCm && heightCm) {
        if (this.currentUnit === 'in') {
          sizeOptionValue.textContent = this._formatInches(widthIn, heightIn);
        } else {
          sizeOptionValue.textContent = this._formatCm(widthCm, heightCm);
        }
      }
    }
  }

  _getSelectionDetails(selection) {
    if (!selection || !selection.size_option) {
      return { available: false, price: null, reason: 'no-selection' };
    }

    // Check if variant is out of stock
    if (!this.currentVariantAvailable) {
      return { available: false, price: null, reason: 'variant-unavailable' };
    }

    // Check if mount is selected without a frame (invalid combination)
    if (selection.border_option === 'mount' && (!selection.framing_option || selection.framing_option === 'unframed')) {
      return { available: false, price: null, reason: 'mount-requires-frame' };
    }

    const sizeComp = this.components.size[selection.size_option];
    let framingComp = this.components.framing[selection.size_option]?.[selection.framing_option];
    const borderComp = this.components.border[selection.size_option]?.[selection.border_option];

    // Special case: "unframed" should always be available with price 0
    if (selection.framing_option === 'unframed' && !framingComp) {
      framingComp = {
        price: 0,
        price_formatted: this._formatPrice(0),
        compare_at_price: 0,
        compare_at_price_formatted: null
      };
    }

    // Check if all required components exist
    // Note: price can be 0, so we check for existence (undefined/null) not truthiness
    const hasSizeComp = sizeComp !== undefined && sizeComp !== null;
    const hasFramingComp = framingComp !== undefined && framingComp !== null;
    const hasBorderComp = borderComp !== undefined && borderComp !== null;

    if (hasSizeComp && hasFramingComp && hasBorderComp) {
      const totalPrice = (sizeComp.price || 0) + (framingComp.price || 0) + (borderComp.price || 0);

      // Check if total price is zero or negative (invalid pricing)
      if (totalPrice <= 0) {
        return { available: false, price: null, reason: 'no-price' };
      }

      // Validate that a fulfillment SKU exists for this combination.
      // Prevents selling configurations that can't be printed (e.g. Bold Canvas in 9x12).
      const artProduct = this.variantConfigurators?.variants?.[this.currentVariantId]?.artProduct || null;
      const prodigiConfig = this._lookupProdigiConfig(
        selection.size_option, selection.framing_option, selection.border_option, artProduct
      );
      if (!prodigiConfig.prodigi_sku) {
        return { available: false, price: null, reason: 'no-fulfillment-config' };
      }

      return {
        available: true,
        price: totalPrice,
        sizePrice: sizeComp.price || 0,
        sizePriceFormatted: sizeComp.price_formatted,
        sizeCompareAtPrice: sizeComp.compare_at_price || 0,
        sizeCompareAtPriceFormatted: sizeComp.compare_at_price_formatted,
        framingPrice: framingComp.price || 0,
        framingPriceFormatted: framingComp.price_formatted,
        framingCompareAtPrice: framingComp.compare_at_price || 0,
        framingCompareAtPriceFormatted: framingComp.compare_at_price_formatted,
        borderPrice: borderComp.price || 0,
        borderPriceFormatted: borderComp.price_formatted,
        borderCompareAtPrice: borderComp.compare_at_price || 0,
        borderCompareAtPriceFormatted: borderComp.compare_at_price_formatted,
      };
    }

    return { available: false, price: null, reason: 'missing-components' };
  }

  _renderOptionRows({ reuseSlider = null } = {}) {
    // Use document fragment for better performance - batch all DOM changes
    const fragment = document.createDocumentFragment();

    try {
      // Use impact slider if product has matching sizes, otherwise standard row
      const enableSlider = this.container.dataset.enableSizeSlider !== 'false';
      const sizeSlider = reuseSlider || (enableSlider ? this._createSizeSlider() : null);
      fragment.appendChild(sizeSlider || this._createRow('size'));
      fragment.appendChild(this._createRow('framing'));

      // Only show border row if there are 2 or more border options available for current size
      const currentSize = this.state.confirmed.size_option;
      const availableBorderOptions = this.components.border[currentSize];
      const availableBorderCount = availableBorderOptions ? Object.keys(availableBorderOptions).length : 0;
      if (availableBorderCount > 1) {
        fragment.appendChild(this._createRow('border'));
      }

      // Single batch DOM update using replaceChildren (more efficient than innerHTML = '')
      // This avoids destroying event listeners and reduces reflow
      requestAnimationFrame(() => {
        this.container.replaceChildren(fragment);

        // Toggle disabled class on container (CSS handles styling, no DOM queries needed)
        this.container.classList.toggle(
          'sku-picker--unavailable',
          !this.currentVariantAvailable || !this.currentSelectionDetails.available
        );

        this._attachEventListeners();
      });
    } catch (error) {
      this.container.innerHTML = '<div style="padding: 20px; color: #d72c2c;">Error rendering configurator: ' + error.message + '</div>';
    }
  }

  _getUnionSizeOptions() {
    const seen = new Set();
    const union = [];
    for (const config of Object.values(this.variantConfigurators.variants)) {
      for (const size of (config.options?.size || [])) {
        if (!seen.has(size.handle)) {
          seen.add(size.handle);
          union.push(size);
        }
      }
    }
    // Sort by max dimension ascending (same order each variant uses)
    union.sort((a, b) => {
      const aMax = Math.max(parseFloat(a.width_inches) || 0, parseFloat(a.height_inches) || 0);
      const bMax = Math.max(parseFloat(b.width_inches) || 0, parseFloat(b.height_inches) || 0);
      return aMax - bMax;
    });
    return union;
  }

  _createSizeSlider() {
    // Build union of sizes across ALL variants so the slider stays stable on variant switch
    const sizeOptions = this._getUnionSizeOptions();
    if (!sizeOptions || sizeOptions.length === 0) return null;

    const slider = document.createElement('size-impact-slider');
    slider.setAttribute('data-size-options', JSON.stringify(sizeOptions));
    slider.setAttribute('data-default-size', this.state.confirmed.size_option || '');
    if (this.container.dataset.enableGalleryWall === 'false') {
      slider.setAttribute('data-enable-gallery-wall', 'false');
    }

    // PDP redesign item 3: Per-bucket minimum price hint.
    // Compute the cheapest *framed* total per size (size price + cheapest framing+border combo).
    // Size buckets: XS ≤12", S 13-16", M 17-32", L 33"+. Serialised as { S: { min: 3300, fmt: 'from $33' } }.
    try {
      const bucketPrices = this._computeBucketMinPrices(sizeOptions);
      if (bucketPrices) {
        slider.setAttribute('data-bucket-prices', JSON.stringify(bucketPrices));
      }
    } catch (e) {
      // Non-fatal: price hints are a progressive enhancement.
      console.warn('[SkuPicker] Could not compute per-bucket price hints:', e);
    }

    return slider;
  }

  _getCurrentSizePrice(sizeHandle) {
    const handle = sizeHandle || this.state?.confirmed?.size_option;
    if (!handle) return null;
    const sizeComp = this.components?.size?.[handle];
    if (!sizeComp || sizeComp.price == null) return null;
    return { raw: sizeComp.price, formatted: this._formatPrice(sizeComp.price) };
  }

  _computeBucketMinPrices(sizeOptions) {
    // _preprocessData() transforms the raw size_pricing / framing_pricing / border_pricing
    // arrays into this.components.{size,framing,border}, keyed by size_option handle.
    const sizePricing = this.components?.size;
    const framingPricing = this.components?.framing || {};
    const borderPricing = this.components?.border || {};
    if (!sizePricing || Object.keys(sizePricing).length === 0) return null;

    // Bucket thresholds mirror size-impact-slider.js SIZE_BUCKETS (keep in sync).
    function bucketFor(size) {
      const maxDim = Math.max(parseFloat(size.width_inches) || 0, parseFloat(size.height_inches) || 0);
      if (maxDim <= 12) return 'XS';
      if (maxDim <= 16) return 'S';
      if (maxDim <= 32) return 'M';
      return 'L';
    }

    const bucketTotals = {};
    for (const size of sizeOptions) {
      const sp = sizePricing[size.handle];
      if (!sp || sp.price == null) continue;

      // Lowest framing add-on for this size
      let minFramingAdd = Infinity;
      const sizeFraming = framingPricing[size.handle] || {};
      for (const key of Object.keys(sizeFraming)) {
        const v = sizeFraming[key]?.price;
        if (typeof v === 'number' && v < minFramingAdd) minFramingAdd = v;
      }
      // If no framing options for this size, treat framing add as 0 (print-only)
      if (!isFinite(minFramingAdd)) minFramingAdd = 0;

      // Lowest border add-on for this size
      let minBorderAdd = Infinity;
      const sizeBorder = borderPricing[size.handle] || {};
      for (const key of Object.keys(sizeBorder)) {
        const v = sizeBorder[key]?.price;
        if (typeof v === 'number' && v < minBorderAdd) minBorderAdd = v;
      }
      if (!isFinite(minBorderAdd)) minBorderAdd = 0;

      const total = sp.price + minFramingAdd + minBorderAdd;
      const bucket = bucketFor(size);
      if (bucketTotals[bucket] == null || total < bucketTotals[bucket]) {
        bucketTotals[bucket] = total;
      }
    }

    // Format prices using the existing _formatPrice helper (respects locale + market currency).
    // `fmt` carries the fully-translated "from $X" hint so the size-impact-slider stays
    // copy-agnostic — the locale-driven template is interpolated here, where translations
    // are available via `_t`.
    const fromTemplate = this._t('size_slider_from_price', 'from {{ price }}');
    const out = {};
    for (const b of Object.keys(bucketTotals)) {
      const formattedPrice = this._formatPrice(bucketTotals[b]);
      out[b] = {
        min: bucketTotals[b],
        fmt: formattedPrice,
        // Pre-rendered display string ("from $33") with the price interpolated.
        display: fromTemplate.replace('{{ price }}', formattedPrice),
      };
    }
    return out;
  }

  _createRow(optionType) {
    const optionHandle = this.state.confirmed[`${optionType}_option`];
    const option = this.data.options[optionType]?.find(opt => opt.handle === optionHandle);
    const priceDetails = this._getSelectionDetails(this.state.confirmed);
    const row = document.createElement('div');
    row.className = optionType === 'border' ? 'option-row option-row--border' : 'option-row';

    let displayLabel = option ? option.label : this._t('select', 'Select');
    if (optionType === 'size') {
      // For size, format with dimensions
      displayLabel = option ? this._formatSizeDimensions(option) : this._t('select', 'Select');
    } else if (optionType === 'framing' && option && option.handle !== 'unframed') {
        const frameShortName = option.short_name || option.label;
        const colorHandle = this.state.confirmed.frame_color;
        let colorShortName = '';
        if (colorHandle && option.colors) {
            const colorOption = option.colors.find(c => c.handle === colorHandle);
            if (colorOption) {
                colorShortName = colorOption.short_name || colorOption.label;
            }
        }
        displayLabel = colorShortName ? `${frameShortName}, ${colorShortName}` : frameShortName;
    }

    let priceHtml = '';
    if (priceDetails.available) {
      if (optionType === 'size') {
        // Show compare_at price with strikethrough if it exists and is higher than current price
        if (priceDetails.sizeCompareAtPrice > 0 && priceDetails.sizeCompareAtPrice > priceDetails.sizePrice) {
          priceHtml = `<span class="price-compare-at">${priceDetails.sizeCompareAtPriceFormatted}</span> <span class="price-discounted">${priceDetails.sizePriceFormatted}</span>`;
        } else {
          priceHtml = priceDetails.sizePriceFormatted;
        }
      }
      else if (optionType === 'framing' && priceDetails.framingPrice > 0) {
        // Show compare_at price with strikethrough if it exists and is higher than current price
        if (priceDetails.framingCompareAtPrice > 0 && priceDetails.framingCompareAtPrice > priceDetails.framingPrice) {
          priceHtml = `<span class="price-compare-at">${priceDetails.framingCompareAtPriceFormatted}</span> <span class="price-discounted">+${priceDetails.framingPriceFormatted}</span>`;
        } else {
          priceHtml = `+${priceDetails.framingPriceFormatted}`;
        }
      }
      else if (optionType === 'border' && priceDetails.borderPrice > 0) {
        // Show compare_at price with strikethrough if it exists and is higher than current price
        if (priceDetails.borderCompareAtPrice > 0 && priceDetails.borderCompareAtPrice > priceDetails.borderPrice) {
          priceHtml = `<span class="price-compare-at">${priceDetails.borderCompareAtPriceFormatted}</span> <span class="price-discounted">+${priceDetails.borderPriceFormatted}</span>`;
        } else {
          priceHtml = `+${priceDetails.borderPriceFormatted}`;
        }
      }
    }

    const guideInfo = this.data.guides[optionType];
    const mainTitle = guideInfo.section_title || (optionType.charAt(0).toUpperCase() + optionType.slice(1));
    const guideLinkText = guideInfo.guide_title || 'Learn More';

    // *** FIX: Wrap the guide link text in a span to control underlining ***
    const guideLinkHTML = `<a href="#" class="info-link" data-guide-for="${optionType}"><span class="info-icon">?</span><span class="link-text">${guideLinkText}</span></a>`;

    if (optionType === 'framing' && optionHandle === 'unframed') {
      // Check if any framing options are available for the current size (excluding 'unframed')
      const currentSize = this.state.confirmed.size_option;
      const availableFramingForSize = this.components.framing[currentSize] || {};
      const availableFrameTypes = Object.keys(availableFramingForSize).filter(key => key !== 'unframed');
      const hasAvailableFraming = availableFrameTypes.length > 0;

      if (hasAvailableFraming) {
        const frameOptionsCount = availableFrameTypes.length;
        const addFrameText = this._t('add_frame', 'Add a handmade frame');
        const optionsAvailableText = this._t('options_available', 'options available');
        row.innerHTML = `<div class="option-left"><span>${mainTitle}</span>${guideLinkHTML}</div><div class="option-main add-frame-button" data-option-name="framing"><div><span class="add-frame-label">${addFrameText}</span><span class="add-frame-sublabel">${frameOptionsCount} ${optionsAvailableText}</span></div><span class="plus-icon">+</span></div>`;
      } else {
        // No framing available for this size - show disabled state
        const unavailableText = this._t('framing_not_available', 'Framing not yet available for this size');
        row.innerHTML = `<div class="option-left"><span>${mainTitle}</span>${guideLinkHTML}</div><div class="option-main add-frame-button disabled" data-option-name="framing"><div><span class="add-frame-label not-available">${unavailableText}</span></div></div>`;
      }
    } else {
      const buttonContent = (optionType === 'framing') ? `<button class="remove-frame-btn" aria-label="Remove frame">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M4 7H20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                <path d="M10 11V17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                <path d="M14 11V17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                <path d="M5 7L6 19C6 19.5304 6.21071 20.0391 6.58579 20.4142C6.96086 20.7893 7.46957 21 8 21H16C16.5304 21 17.0391 20.7893 17.4142 20.4142C17.7893 20.0391 18 19.5304 18 19L19 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                <path d="M9 7V4C9 3.73478 9.10536 3.48043 9.29289 3.29289C9.48043 3.10536 9.73478 3 10 3H14C14.2652 3 14.5196 3.10536 14.7071 3.29289C14.8946 3.48043 15 3.73478 15 4V7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </button>` : `<span class="dropdown-arrow">▼</span>`;

      // Add unit toggle for size option (after the title)
      if (optionType === 'size') {
        const unitToggle = this._createUnitToggle();
        row.appendChild(document.createRange().createContextualFragment(`<div class="option-left"></div>`));
        const optionLeft = row.querySelector('.option-left');
        optionLeft.insertAdjacentHTML('beforeend', `<span>${mainTitle}</span>`);
        optionLeft.appendChild(unitToggle);
        optionLeft.insertAdjacentHTML('beforeend', guideLinkHTML);
        row.insertAdjacentHTML('beforeend', `<div class="option-main" data-option-name="${optionType}"><span class="option-value">${displayLabel}</span><span class="option-price">${priceHtml}</span>${buttonContent}</div>`);
        return row;
      }

      row.innerHTML = `<div class="option-left"><span>${mainTitle}</span>${guideLinkHTML}</div><div class="option-main" data-option-name="${optionType}"><span class="option-value">${displayLabel}</span><span class="option-price">${priceHtml}</span>${buttonContent}</div>`;
    }
    return row;
  }

  _attachEventListeners() {
    // No-op: Event delegation is now handled by _setupEventDelegation() in _initialize()
    // This method is kept for backwards compatibility with existing call sites
  }

  _openModal(optionType) {
    // Save pre-modal state so we can revert on cancel (Escape / overlay click)
    this._preModalState = { ...this.state.confirmed };
    this.state.temporary = { ...this.state.confirmed };
    this._modalOpen = true;

    const templateId = `${optionType}-modal-template`;
    const ariaLabels = { size: 'Select size', framing: 'Select frame', border: 'Select border' };
    const modalContent = this.modal.open(templateId, ariaLabels[optionType] || 'Product options');

    // Register close callback to revert state if the user didn't confirm
    let confirmed = false;
    this.modal._onCloseCallback = () => {
      this._modalOpen = false;
      if (!confirmed) {
        // Revert to pre-modal state
        this.state.confirmed = { ...this._preModalState };
        this.state.temporary = { ...this._preModalState };
        this._renderOptionRows();
        this._updateConfirmedSelection(true);
      }
    };

    // Dispatch event when modal opens (for lazy-loading mocks)
    const event = new CustomEvent('sku-picker:modal-opened', {
      detail: { optionType }
    });
    document.dispatchEvent(event);

    if (modalContent) {
      this._renderModalContent(modalContent, optionType);
      modalContent.querySelector('.select-button').addEventListener('click', () => {
        if (this._getSelectionDetails(this.state.temporary).available) {
          confirmed = true;
          this.state.confirmed = { ...this.state.temporary };
          // Reset border to 'no-border' if framing is 'unframed' and border is 'mount'
          if (this.state.confirmed.framing_option === 'unframed' && this.state.confirmed.border_option === 'mount') {
            this.state.confirmed.border_option = 'no-border';
          }
          this._publishSelectedOptionEvent(optionType);
          this.modal.close();
        }
      });
    }
  }

  _openGuideModal(guideType) {
    const guideData = this.data.guides[guideType];
    // *** FIX: Loosen the guard clause to allow opening even if content is null ***
    if (!guideData) return;

    const modalEl = this.modal.open('guide-modal-template');
    if (modalEl) {
        // Add guide type class for styling
        modalEl.classList.add(`guide-modal--${guideType}`);

        const titleEl = modalEl.querySelector('.guide-title');
        const contentEl = modalEl.querySelector('.guide-content');
        const modalHeader = modalEl.querySelector('.modal-header');

        if (titleEl) {
          // Use the guide_title for the modal header
          titleEl.textContent = guideData.guide_title || 'Information';
        }

        // Helper to populate modal content (used immediately and after async load)
        const populateContent = () => {
          if (!contentEl) return;
          if (guideType === 'size') {
            const content = this._getSizeGuideContent();
            contentEl.innerHTML = content || '<p>Guide content is not available at this time.</p>';
          } else {
            contentEl.innerHTML = guideData.content || '<p>Guide content is not available at this time.</p>';
          }
        };

        // For size guide, add unit toggle and display content based on currentUnit
        if (guideType === 'size') {
          // Add unit toggle to header
          let unitToggle = modalHeader.querySelector('.unit-toggle');

          if (!unitToggle) {
            const toggle = document.createElement('span');
            toggle.className = 'unit-toggle';
            toggle.innerHTML = `<button type="button" class="unit-option ${this.currentUnit === 'cm' ? 'active' : ''}" data-unit="cm">cm</button><span class="separator">|</span><button type="button" class="unit-option ${this.currentUnit === 'in' ? 'active' : ''}" data-unit="in">in</button>`;
            unitToggle = toggle;

            unitToggle.style.position = 'absolute';
            unitToggle.style.left = '20px';
            unitToggle.style.top = '50%';
            unitToggle.style.transform = 'translateY(-50%)';

            // Attach event listeners for unit switching
            unitToggle.querySelectorAll('.unit-option').forEach((btn) => {
              btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const newUnit = btn.dataset.unit;

                if (newUnit !== this.currentUnit) {
                  this._setUnitPreference(newUnit);

                  // Update toggle active state
                  unitToggle.querySelectorAll('.unit-option').forEach(b => {
                    b.classList.toggle('active', b.dataset.unit === newUnit);
                  });

                  // Update content based on new unit with smooth transition
                  const content = this._getSizeGuideContent();
                  if (contentEl) {
                    // Add fade effect to prevent flash
                    contentEl.style.opacity = '0';
                    contentEl.style.transition = 'opacity 0.15s ease-in-out';

                    setTimeout(() => {
                      contentEl.innerHTML = content || '<p>Guide content is not available at this time.</p>';
                      contentEl.style.opacity = '1';
                    }, 150);
                  }

                  // Update the main page display
                  const hasSSRContent = this.container.querySelectorAll('.option-row').length > 0;
                  if (hasSSRContent) {
                    this._updateSSRSizeDisplay();
                    // Update the toggle on the main page
                    const mainToggle = this.container.querySelector('.unit-toggle');
                    if (mainToggle) {
                      mainToggle.querySelectorAll('.unit-option').forEach(mainBtn => {
                        mainBtn.classList.toggle('active', mainBtn.dataset.unit === newUnit);
                      });
                    }
                  } else {
                    this._updateDynamicSizeDisplay();
                  }
                }
              });
            });

            modalHeader.appendChild(unitToggle);
          }
        }

        // Check if guide content is available yet (may still be loading in background)
        const hasContent = guideType === 'size'
          ? !!(this._getSizeGuideContent())
          : !!(guideData.content);

        if (hasContent) {
          populateContent();
        } else if (this._articleContentPromise) {
          // Content still loading — show loading indicator, then populate when ready
          if (contentEl) contentEl.innerHTML = '<p style="text-align:center;color:#999;">Loading…</p>';
          this._articleContentPromise.then(() => populateContent());
        } else {
          populateContent();
        }
    }
  }

  _getSizeGuideContent() {
    // Choose content based on currentUnit
    // If inches preferred and size_inches content exists, use it; otherwise fallback to size content
    if (this.currentUnit === 'in' && this.data.guides.size_inches && this.data.guides.size_inches.content) {
      return this.data.guides.size_inches.content;
    }
    // Fallback to cm content (default size guide)
    return this.data.guides.size ? this.data.guides.size.content : null;
  }

  _renderModalContent(modalEl, optionType) {

    if (optionType === 'size') {
      // Add unit indicator to modal title and unit toggle in header
      const modalHeader = modalEl.querySelector('.modal-header');
      const h3 = modalHeader.querySelector('h3');
      const unitLabel = this.currentUnit === 'in' ?
        this._t('unit_inches', '(inches)') :
        this._t('unit_cms', '(cms)');
      const selectSizeText = this._t('select_size', 'Select Size');

      h3.textContent = `${selectSizeText} ${unitLabel}`;

      // Check if toggle already exists
      let unitToggle = modalHeader.querySelector('.unit-toggle');

      if (!unitToggle) {
        // Create toggle for the first time (without event listeners for modal)
        const toggle = document.createElement('span');
        toggle.className = 'unit-toggle';
        toggle.innerHTML = `<button type="button" class="unit-option ${this.currentUnit === 'cm' ? 'active' : ''}" data-unit="cm">cm</button><span class="separator">|</span><button type="button" class="unit-option ${this.currentUnit === 'in' ? 'active' : ''}" data-unit="in">in</button>`;
        unitToggle = toggle;

        unitToggle.style.position = 'absolute';
        unitToggle.style.left = '20px';
        unitToggle.style.top = '50%';
        unitToggle.style.transform = 'translateY(-50%)';

        // Attach event listeners
        unitToggle.querySelectorAll('.unit-option').forEach((btn, btnIndex) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const newUnit = btn.dataset.unit;

            if (newUnit !== this.currentUnit) {
              this._setUnitPreference(newUnit);

              // Update the main page display
              const hasSSRContent = this.container.querySelectorAll('.option-row').length > 0;
              if (hasSSRContent) {
                this._updateSSRSizeDisplay();
                // Update the toggle on the main page
                const mainToggle = this.container.querySelector('.unit-toggle');
                if (mainToggle) {
                  mainToggle.querySelectorAll('.unit-option').forEach(mainBtn => {
                    mainBtn.classList.toggle('active', mainBtn.dataset.unit === newUnit);
                  });
                }
              } else {
                this._renderOptionRows();
              }

              // Re-render modal content with new unit
              this._renderModalContent(modalEl, optionType);
            } else {
            }
          });
        });

        modalHeader.appendChild(unitToggle);
      } else {
        // Toggle already exists, just update the active states
        unitToggle.querySelectorAll('.unit-option').forEach(btn => {
          const isActive = btn.dataset.unit === this.currentUnit;
          btn.classList.toggle('active', isActive);
        });
      }

      // Render size options grid
      const grid = modalEl.querySelector('.option-grid-container');
      grid.innerHTML = '';
      grid.setAttribute('role', 'radiogroup');
      grid.setAttribute('aria-label', this._t('select_size', 'Select Size'));
      const options = this.data.options.size;
      options.forEach(option => {
        const optionButton = this._createSizeOptionButton(option);

        optionButton.addEventListener('click', () => {
          if (optionButton.classList.contains('disabled')) return;
          this.state.temporary.size_option = option.handle;
          const isFrameAvailable = !!this.components.framing[this.state.temporary.size_option]?.[this.state.temporary.framing_option];
          if (!isFrameAvailable) {
            this.state.temporary.framing_option = 'unframed';
            this.state.temporary.frame_color = null;
            // Reset border to 'no-border' if it's 'mount' (mount requires frame)
            if (this.state.temporary.border_option === 'mount') {
              this.state.temporary.border_option = 'no-border';
            }
          }
          const isBorderAvailable = !!this.components.border[this.state.temporary.size_option]?.[this.state.temporary.border_option];
          if (!isBorderAvailable) { this.state.temporary.border_option = 'no-border'; }
          this._updateModalUI(modalEl, optionType);
          this._updateLivePreview(); // Update mock immediately
        });
        grid.appendChild(optionButton);
      });
      this._updateModalUI(modalEl, optionType);

    } else if (optionType === 'framing') {
      // Set initial state if currently unframed - select first available framing option
      if (this.state.temporary.framing_option === 'unframed') {
        const currentSize = this.state.temporary.size_option;
        const firstAvailableFramedOption = this.data.options.framing.find(f => {
          if (f.handle === 'unframed') return false;
          // Check if this framing option is available for the current size
          return !!this.components.framing[currentSize]?.[f.handle];
        });
        if (firstAvailableFramedOption) {
          this.state.temporary.framing_option = firstAvailableFramedOption.handle;
          this.state.temporary.frame_color = firstAvailableFramedOption.colors?.[0]?.handle || null;
        }
      }

      // Dynamically create frame type tabs
      const tabContainer = modalEl.querySelector('.frame-type-selector');
      tabContainer.innerHTML = '';

      // Check if desktop (use stacked sections) or mobile (use horizontal tabs)
      const isDesktop = window.innerWidth > 768;

      // Sort framing options: available first, unavailable last
      const currentSize = this.state.temporary.size_option;
      const sortedFramingOptions = [...this.data.options.framing].sort((a, b) => {
        if (a.handle === 'unframed') return 1; // Unframed always last (skipped anyway)
        if (b.handle === 'unframed') return -1;
        const aAvailable = !!this.components.framing[currentSize]?.[a.handle];
        const bAvailable = !!this.components.framing[currentSize]?.[b.handle];
        if (aAvailable && !bAvailable) return -1;
        if (!aAvailable && bAvailable) return 1;
        return 0; // Keep original order for items with same availability
      });

      sortedFramingOptions.forEach(framingOption => {
        if (framingOption.handle === 'unframed') return; // Skip unframed

        if (isDesktop) {
          // Desktop: Create section wrapper with embedded content
          const section = document.createElement('div');
          section.className = 'frame-type-section';
          section.dataset.frameHandle = framingOption.handle;

          const tab = document.createElement('button');
          tab.type = 'button';
          tab.className = 'frame-type-tab';
          tab.dataset.frameHandle = framingOption.handle;

          const tabLabel = document.createElement('div');
          tabLabel.className = 'tab-label';

          const typeName = document.createElement('span');
          typeName.className = 'type-name';
          typeName.textContent = framingOption.short_name || framingOption.label;
          tabLabel.appendChild(typeName);

          if (framingOption.is_new) {
            const badge = document.createElement('span');
            badge.className = 'badge new';
            badge.textContent = 'New';
            tabLabel.appendChild(badge);
          }

          const typePrice = document.createElement('div');
          typePrice.className = 'type-price';
          tab.appendChild(tabLabel);
          tab.appendChild(typePrice);

          tab.addEventListener('click', () => {
            if (tab.disabled) return;
            this.state.temporary.framing_option = framingOption.handle;
            this.state.temporary.frame_color = framingOption.colors?.[0]?.handle || null;
            this._updateModalUI(modalEl, 'framing');
            this._updateLivePreview();
          });

          const content = document.createElement('div');
          content.className = 'frame-type-content';

          const colorsGrid = document.createElement('div');
          colorsGrid.className = 'frame-colors-grid';

          const description = document.createElement('div');
          description.className = 'frame-description';

          content.appendChild(colorsGrid);
          content.appendChild(description);

          section.appendChild(tab);
          section.appendChild(content);
          tabContainer.appendChild(section);
        } else {
          // Mobile: Create simple tab button (content in shared area)
          const tab = document.createElement('button');
          tab.type = 'button';
          tab.className = 'frame-type-tab';
          tab.dataset.frameHandle = framingOption.handle;

          const tabLabel = document.createElement('div');
          tabLabel.className = 'tab-label';

          const typeName = document.createElement('span');
          typeName.className = 'type-name';
          typeName.textContent = framingOption.short_name || framingOption.label;
          tabLabel.appendChild(typeName);

          if (framingOption.is_new) {
            const badge = document.createElement('span');
            badge.className = 'badge new';
            badge.textContent = 'New';
            tabLabel.appendChild(badge);
          }

          const typePrice = document.createElement('div');
          typePrice.className = 'type-price';
          tab.appendChild(tabLabel);
          tab.appendChild(typePrice);

          tab.addEventListener('click', () => {
            if (tab.disabled) return;
            this.state.temporary.framing_option = framingOption.handle;
            this.state.temporary.frame_color = framingOption.colors?.[0]?.handle || null;
            this._updateModalUI(modalEl, 'framing');
            this._updateLivePreview();
          });

          tabContainer.appendChild(tab);
        }
      });

      this._updateModalUI(modalEl, 'framing');

    } else { // Handles 'border'
      const grid = modalEl.querySelector('.option-grid-container');
      grid.innerHTML = '';
      grid.setAttribute('role', 'radiogroup');
      grid.setAttribute('aria-label', this._t('select_border', 'Select border'));
      const options = this.data.options[optionType];
      options.forEach(option => {
        const optionButton = this._createBorderOptionButton(option);

        optionButton.addEventListener('click', () => {
          if (optionButton.classList.contains('disabled')) return;
          this.state.temporary[`${optionType}_option`] = option.handle;
          this._updateModalUI(modalEl, optionType);
          this._updateLivePreview(); // Update mock immediately
        });
        grid.appendChild(optionButton);
      });
      this._updateModalUI(modalEl, optionType);
    }
  }

  _createSizeOptionButton(option) {
    const button = document.createElement('button');
    button.className = 'selectable size-option-button';
    button.dataset.optionHandle = option.handle;
    button.setAttribute('role', 'radio');
    button.setAttribute('aria-checked', 'false');
    const dims = this._formatSizeDimensions(option);
    button.innerHTML = `<span class="size-option-dims">${dims || option.label}</span>`;
    return button;
  }

  _createBorderOptionButton(option) {
    const button = document.createElement('button');
    button.className = 'border-option-button selectable';
    button.dataset.borderHandle = option.handle;
    button.setAttribute('role', 'radio');
    button.setAttribute('aria-checked', 'false');
    const thumbnailHTML = option.thumbnail
      ? `<img src="${option.thumbnail}" alt="${option.label} thumbnail">`
      : `<div class="thumbnail-placeholder"></div>`;
    const newBadgeHTML = option.is_new ? `<span class="badge new">New</span>` : '';
    button.innerHTML = `
      <div class="border-option-thumbnail">
        ${thumbnailHTML}
        ${newBadgeHTML}
      </div>
      <span class="border-option-name">${option.label}</span>
      <div class="border-option-price"></div>
    `;
    return button;
  }
  
  _renderFrameDetails(modalEl) {
    const isDesktop = window.innerWidth > 768;
    const { framing_option } = this.state.temporary;

    if (isDesktop) {
      // Desktop: Render content in all sections
      this.data.options.framing.forEach(framingOption => {
        if (framingOption.handle === 'unframed') return;

        const section = modalEl.querySelector(`.frame-type-section[data-frame-handle="${framingOption.handle}"]`);
        if (!section) return;

        const colorsGrid = section.querySelector('.frame-colors-grid');
        const descriptionEl = section.querySelector('.frame-description');

        if (!colorsGrid || !descriptionEl) return;

        colorsGrid.innerHTML = '';
        descriptionEl.innerHTML = '';

        if (framingOption.colors && framingOption.colors.length > 0) {
          let colorsHTML = '';
          framingOption.colors.forEach(color => {
            const swatchStyle = color.thumbnail
              ? `background-image: url('${color.thumbnail}');`
              : `background-color: ${color.color_value || '#f5f5f5'};`;

            colorsHTML += `
              <button type="button" class="color-option" data-color-handle="${color.handle}" data-frame-handle="${framingOption.handle}">
                <div class="color-swatch" style="${swatchStyle}"></div>
                <span class="color-name">${color.short_name || color.label}</span>
              </button>
            `;
          });
          colorsGrid.innerHTML = colorsHTML;

          colorsGrid.querySelectorAll('.color-option').forEach(opt => {
            opt.addEventListener('click', (e) => {
                if (opt.classList.contains('disabled')) return;
                e.stopPropagation();
                this.state.temporary.framing_option = opt.dataset.frameHandle;
                this.state.temporary.frame_color = opt.dataset.colorHandle;
                this._updateModalUI(modalEl, 'framing');
                this._updateLivePreview();
            });
          });
        }

        if (framingOption.description) {
          descriptionEl.innerHTML = `<span class="icon-leaf"></span><p>${framingOption.description}</p>`;
        }
      });
    } else {
      // Mobile: Render in shared frame-details-section for active tab only
      const colorsGrid = modalEl.querySelector('.frame-details-section .frame-colors-grid');
      const descriptionEl = modalEl.querySelector('.frame-details-section .frame-description');

      if (!colorsGrid || !descriptionEl) return;

      colorsGrid.innerHTML = '';
      descriptionEl.innerHTML = '';

      const frameData = this.data.options.framing.find(f => f.handle === framing_option);
      if (!frameData) return;

      if (frameData.colors && frameData.colors.length > 0) {
        let colorsHTML = '';
        frameData.colors.forEach(color => {
          const swatchStyle = color.thumbnail
            ? `background-image: url('${color.thumbnail}');`
            : `background-color: ${color.color_value || '#f5f5f5'};`;

          colorsHTML += `
            <button type="button" class="color-option" data-color-handle="${color.handle}">
              <div class="color-swatch" style="${swatchStyle}"></div>
              <span class="color-name">${color.short_name || color.label}</span>
            </button>
          `;
        });
        colorsGrid.innerHTML = colorsHTML;

        colorsGrid.querySelectorAll('.color-option').forEach(opt => {
          opt.addEventListener('click', (e) => {
              if (opt.classList.contains('disabled')) return;
              e.stopPropagation();
              this.state.temporary.frame_color = opt.dataset.colorHandle;
              this._updateModalUI(modalEl, 'framing');
              this._updateLivePreview();
          });
        });
      }

      if (frameData.description) {
        descriptionEl.innerHTML = `<span class="icon-leaf"></span><p>${frameData.description}</p>`;
      }
    }
  }

  /**
   * Updates the live preview by broadcasting the temporary state for mock
   * updates without overwriting the confirmed state. The confirmed state is
   * only committed when the user clicks "Select" in the modal.
   */
  _updateLivePreview() {
    // Temporarily swap confirmed state for the preview broadcast, then restore.
    // This lets _updateConfirmedSelection emit the correct mock/price preview
    // while keeping the real confirmed state intact for revert-on-cancel.
    this.state.confirmed = { ...this.state.temporary };

    // Update option rows display immediately (this is quick, just updating classes/text)
    this._renderOptionRows();

    // Defer heavy gallery mock updates to avoid blocking interaction
    requestAnimationFrame(() => {
      this._updateConfirmedSelection(true); // true = user-initiated for preview
      // Restore confirmed state to the pre-modal snapshot if modal is still open.
      // Using _preModalState (set once at modal open) avoids a race where rapid
      // taps within a single frame would capture stale intermediate state.
      if (this._modalOpen && this._preModalState) {
        this.state.confirmed = { ...this._preModalState };
      }
    });
  }

  _updateModalUI(modalEl, optionType) {
    // Debounce rapid UI updates to prevent blocking
    if (this._updateUITimeout) {
      cancelAnimationFrame(this._updateUITimeout);
    }

    this._updateUITimeout = requestAnimationFrame(() => {
      this._performModalUIUpdate(modalEl, optionType);
    });
  }

  _performModalUIUpdate(modalEl, optionType) {
    const selectButton = modalEl.querySelector('.select-button');

    if (optionType === 'framing') {
      const { framing_option, frame_color, size_option } = this.state.temporary;
      const isDesktop = window.innerWidth > 768;

      // Always render frame details
      this._renderFrameDetails(modalEl);

      if (isDesktop) {
        // Desktop: Update sections and embedded tabs
        modalEl.querySelectorAll('.frame-type-section').forEach(section => {
          const handle = section.dataset.frameHandle;
          const tab = section.querySelector('.frame-type-tab');
          const priceEl = tab?.querySelector('.type-price');
          const frameComp = this.components.framing[size_option]?.[handle];

          section.classList.toggle('active', handle === framing_option);

          if (tab) {
            tab.classList.toggle('active', handle === framing_option);
          }

          if (priceEl) {
            if (frameComp) {
              let priceHTML = '';
              const { price, price_formatted, compare_at_price, compare_at_price_formatted } = frameComp;
              if (compare_at_price && compare_at_price > price) {
                priceHTML = `<span class="strikethrough">${compare_at_price_formatted}</span> <span class="sale">+${price_formatted}</span>`;
              } else {
                priceHTML = `<span class="sale">+${price_formatted}</span>`;
              }
              priceEl.innerHTML = priceHTML;
              if (tab) {
                tab.disabled = false;
                const badge = tab.querySelector('.badge.new');
                if (badge) badge.style.display = '';
              }
            } else {
              priceEl.innerHTML = `<span class="not-available">Unavailable in this size</span>`;
              if (tab) {
                tab.disabled = true;
                const badge = tab.querySelector('.badge.new');
                if (badge) badge.style.display = 'none';
              }
            }
          }
        });

        // Update color option active and disabled states (desktop)
        modalEl.querySelectorAll('.color-option').forEach(el => {
          const isActiveFrame = el.dataset.frameHandle === framing_option;
          const isActiveColor = el.dataset.colorHandle === frame_color;
          el.classList.toggle('active', isActiveFrame && isActiveColor);

          // Check if this specific frame color combination is available
          const frameHandle = el.dataset.frameHandle;
          const colorHandle = el.dataset.colorHandle;
          const testSelection = {
            ...this.state.temporary,
            framing_option: frameHandle,
            frame_color: colorHandle
          };
          const details = this._getSelectionDetails(testSelection);
          const unavailable = !details.available;
          el.classList.toggle('disabled', unavailable);
          el.setAttribute('aria-disabled', String(unavailable));
        });
      } else {
        // Mobile: Update tabs in selector
        modalEl.querySelectorAll('.frame-type-tab').forEach(tab => {
          const handle = tab.dataset.frameHandle;
          const priceEl = tab.querySelector('.type-price');
          const frameComp = this.components.framing[size_option]?.[handle];

          tab.classList.toggle('active', handle === framing_option);

          if (priceEl) {
            if (frameComp) {
              let priceHTML = '';
              const { price, price_formatted, compare_at_price, compare_at_price_formatted } = frameComp;
              if (compare_at_price && compare_at_price > price) {
                priceHTML = `<span class="strikethrough">${compare_at_price_formatted}</span> <span class="sale">+${price_formatted}</span>`;
              } else {
                priceHTML = `<span class="sale">+${price_formatted}</span>`;
              }
              priceEl.innerHTML = priceHTML;
              tab.disabled = false;
              tab.style.display = '';
              const badge = tab.querySelector('.badge.new');
              if (badge) badge.style.display = '';
            } else {
              // On mobile, hide unavailable options entirely (not enough space)
              tab.disabled = true;
              tab.style.display = 'none';
            }
          }
        });

        // Update color option active and disabled states (mobile)
        modalEl.querySelectorAll('.color-option').forEach(el => {
          el.classList.toggle('active', el.dataset.colorHandle === frame_color);

          // Check if this specific frame color is available with current framing option
          const colorHandle = el.dataset.colorHandle;
          const testSelection = {
            ...this.state.temporary,
            frame_color: colorHandle
          };
          const details = this._getSelectionDetails(testSelection);
          el.classList.toggle('disabled', !details.available);
        });
      }

    } else { // Handles 'size' and 'border'
      const grid = modalEl.querySelector('.option-grid-container');
      const tempKey = `${optionType}_option`;

      // Show/hide mount notice when in border modal and framing is unframed
      if (optionType === 'border') {
        const mountNotice = modalEl.querySelector('.mount-notice');
        if (mountNotice) {
          const isUnframed = !this.state.temporary.framing_option || this.state.temporary.framing_option === 'unframed';
          mountNotice.style.display = isUnframed ? 'flex' : 'none';
          if (isUnframed) {
            const noticeText = mountNotice.querySelector('.mount-notice-text');
            if (noticeText) {
              noticeText.textContent = this._t('mount_requires_frame', 'Mounts are only available for framed art');
            }
          }
        }
      }

      grid.querySelectorAll('.selectable').forEach(button => {
        const handle = button.dataset.optionHandle || button.dataset.borderHandle;
        const isSelected = handle === this.state.temporary[tempKey];
        button.classList.toggle('selected', isSelected);
        button.setAttribute('aria-checked', String(isSelected));
        const testSelection = { ...this.state.temporary, [tempKey]: handle };
        const details = this._getSelectionDetails(testSelection);
        const unavailable = !details.available;
        button.classList.toggle('disabled', unavailable);
        button.setAttribute('aria-disabled', String(unavailable));
        if (optionType === 'border') {
          const { size_option } = this.state.temporary;
          const priceEl = button.querySelector('.border-option-price');
          const borderComp = this.components.border[size_option]?.[handle];
          if (priceEl) {
            priceEl.innerHTML = '';
            if (borderComp) {
                let priceHTML = '';
                const { price, price_formatted, compare_at_price, compare_at_price_formatted } = borderComp;
                if (price > 0) {
                  if (compare_at_price && compare_at_price > price) {
                      priceHTML = `<span class="strikethrough">${compare_at_price_formatted}</span> <span class="sale">+${price_formatted}</span>`;
                  } else {
                      priceHTML = `<span class="sale">+${price_formatted}</span>`;
                  }
                } else {
                  priceHTML = `<span class="free">Free</span>`;
                }
                priceEl.innerHTML = priceHTML;
            } else if (!details.available) {
              priceEl.innerHTML = `<span class="unavailable">Unavailable</span>`;
            }
          }
        }
      });
    }

    const finalTempDetails = this._getSelectionDetails(this.state.temporary);
    selectButton.disabled = !finalTempDetails.available;
    if (optionType === 'framing') {
        selectButton.innerHTML = this._t('select_frame', 'Select Frame');
    } else if (optionType === 'border') {
        const selectedOption = this.data.options.border.find(opt => opt.handle === this.state.temporary.border_option);
        const buttonLabel = selectedOption ? selectedOption.label.toLowerCase() : '';
        selectButton.innerHTML = `${this._t('select', 'Select')} ${buttonLabel}`;
        if (!finalTempDetails.available) { selectButton.innerHTML = this._t('unavailable', 'Unavailable'); }
    } else if (optionType === 'size') {
      // For size modal, show only the size price (not total)
      if (finalTempDetails.available) {
        let priceHtml = '';
        // Show compare_at price with strikethrough if it exists and is higher than current price
        if (finalTempDetails.sizeCompareAtPrice > 0 && finalTempDetails.sizeCompareAtPrice > finalTempDetails.sizePrice) {
          priceHtml = `<span class="price-compare-at">${finalTempDetails.sizeCompareAtPriceFormatted}</span> <span class="price-discounted">${finalTempDetails.sizePriceFormatted}</span>`;
        } else {
          priceHtml = finalTempDetails.sizePriceFormatted;
        }
        selectButton.innerHTML = `${priceHtml} &middot; ${this._t('select', 'Select')}`;
      } else {
        selectButton.innerHTML = this._t('unavailable', 'Unavailable');
      }
    } else if (finalTempDetails.available) {
      selectButton.innerHTML = `${this._formatTotalPrice(finalTempDetails.price)} &middot; ${this._t('select', 'Select')}`;
    } else {
      selectButton.innerHTML = this._t('unavailable', 'Unavailable');
    }
  }

  _updateConfirmedSelection(userInitiated = false, { skipUrl = false } = {}) {
    this.currentSelectionDetails = this._getSelectionDetails(this.state.confirmed);
    this._updateFinalPrice();
    this._updateFormProperties();
    if (!skipUrl) this._updateURL();
    this._emitSelectionChanged(userInitiated);
    this._broadcastMockConfig();
  }

  _emitSelectionChanged(userInitiated = false) {
    const event = new CustomEvent('sku-picker:selection-changed', {
      bubbles: true,
      detail: {
        selection: {
          size_option: this.state.confirmed.size_option,
          framing_option: this.state.confirmed.framing_option,
          border_option: this.state.confirmed.border_option,
          frame_color: this.state.confirmed.frame_color
        },
        userInitiated: userInitiated
      }
    });
    this.container.dispatchEvent(event);
  }

  /**
   * Public API: returns the confirmed size's physical dimensions.
   * Other components (style-picker, size-slider) use this to include
   * wallDimensions in gallery-wall:config-changed events.
   */
  getSizeDimensions() {
    const sizeOpt = this.data?.options?.size?.find(
      s => s.handle === this.state.confirmed.size_option
    );
    if (!sizeOpt) return null;
    const widthInches = parseFloat(sizeOpt.width_inches);
    const heightInches = parseFloat(sizeOpt.height_inches);
    if (!widthInches || !heightInches) return null;
    return { size: this.state.confirmed.size_option, widthInches, heightInches };
  }

  /**
   * Resolves the current confirmed state into the detail shape that
   * gallery-wall:config-changed expects (hex, moulding, border, productType).
   */
  getResolvedMockConfig() {
    if (!this.data) return null;
    const framingOpt = (this.data.options?.framing || [])
      .find(f => f.handle === this.state.confirmed.framing_option);
    const color = framingOpt?.colors?.find(c => c.handle === this.state.confirmed.frame_color);
    const isUnframed = this.state.confirmed.framing_option === 'unframed'
      || this.state.confirmed.framing_option?.endsWith('-unframed');
    return {
      frameColorHex: color?.color_value
        || FRAME_COLOR_HEX[color?.short_name?.toLowerCase()] || '#333',
      mouldingTile:  isUnframed ? '' : (color?.moulding_tile || ''),
      mouldingWidth: isUnframed ? 0  : (color?.moulding_width || null),
      hasFrame:      !isUnframed,
      borderOption:  this.state.confirmed.border_option,
      borderWidth:   this._getBorderWidthCm(),
      productType:   this.data.artProduct === 'canvas-print' ? 'canvas' : 'art_print',
    };
  }

  _getBorderWidthCm() {
    if (this.state.confirmed.border_option === 'mount') return this._getMountWidthCm();
    if (this.state.confirmed.border_option === 'border') return 2; // WHITE_BORDER_WIDTH_CM
    return 0;
  }

  _getMountWidthCm() {
    const sizeOpt = this.data?.options?.size?.find(
      s => s.handle === this.state.confirmed.size_option
    );
    const maxDim = Math.max(
      parseFloat(sizeOpt?.width_inches) || 0,
      parseFloat(sizeOpt?.height_inches) || 0
    );
    if (maxDim <= 10) return 2.5;
    if (maxDim <= 15) return 4;
    return 5;
  }

  _broadcastMockConfig() {
    const mockConfig = this.getResolvedMockConfig();
    if (!mockConfig) return;

    this.lastMockConfig = mockConfig;
    document.dispatchEvent(new CustomEvent('gallery-wall:config-changed', {
      bubbles: true, detail: mockConfig,
    }));

    const sizeDims = this.getSizeDimensions();
    if (sizeDims) {
      const sizeDetail = {
        size: sizeDims.size,
        wallDimensions: { widthInches: sizeDims.widthInches, heightInches: sizeDims.heightInches },
        artworkWidth: sizeDims.widthInches,
      };
      this.lastSizeConfig = sizeDetail;
      document.dispatchEvent(new CustomEvent('gallery-wall:config-changed', {
        bubbles: true, detail: sizeDetail,
      }));
    }
  }

  _publishSelectedOptionEvent(optionType) {
    // Defer analytics to idle time to avoid blocking interactions
    const publishAnalytics = () => {
      // Check if Shopify analytics is available
      if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') {
        return;
      }

      // Get the variant configuration data
      const variantConfig = this.variantConfigurators.variants[this.currentVariantId];
      if (!variantConfig) {
        return;
      }

      // Build the selected option details
      const sizeOption = this.data.options.size?.find(s => s.handle === this.state.confirmed.size_option);
      const framingOption = this.data.options.framing?.find(f => f.handle === this.state.confirmed.framing_option);
      const borderOption = this.data.options.border?.find(b => b.handle === this.state.confirmed.border_option);
      const frameColorOption = framingOption?.colors?.find(c => c.handle === this.state.confirmed.frame_color);

      // Get price details
      const priceDetails = this._getSelectionDetails(this.state.confirmed);

      // Get currency code from Shopify
      const currencyCode = window.Shopify?.currency?.active || 'USD';

      // Build the event payload matching Shopify analytics format
      const payload = {
        optionType: optionType,
        productVariant: {
          id: this.currentVariantId,
          title: variantConfig.variantTitle,
          price: {
            amount: (priceDetails.price || 0) / 100, // Convert from cents to dollars
            currencyCode: currencyCode
          }
        },
        selection: {
          size: {
            handle: this.state.confirmed.size_option,
            label: sizeOption?.label || null,
            price: {
              amount: (priceDetails.sizePrice || 0) / 100,
              currencyCode: currencyCode
            }
          },
          framing: {
            handle: this.state.confirmed.framing_option,
            label: framingOption?.label || null,
            price: {
              amount: (priceDetails.framingPrice || 0) / 100,
              currencyCode: currencyCode
            }
          },
          border: {
            handle: this.state.confirmed.border_option,
            label: borderOption?.label || null,
            price: {
              amount: (priceDetails.borderPrice || 0) / 100,
              currencyCode: currencyCode
            }
          },
          frameColor: {
            handle: this.state.confirmed.frame_color,
            label: frameColorOption?.label || null
          }
        }
      };

      // Dispatch DOM event for PostHog tracking
      document.dispatchEvent(new CustomEvent('sku-picker:selection-confirmed', {
        detail: {
          optionType: optionType,
          productId: this.data.productId,
          variantId: this.currentVariantId,
          selection: payload.selection
        }
      }));

      // Publish the event
      window.Shopify.analytics.publish('selected_option', payload);
    };

    // Use requestIdleCallback to defer analytics during idle time
    // Fallback to setTimeout if requestIdleCallback not available
    if ('requestIdleCallback' in window) {
      requestIdleCallback(publishAnalytics, { timeout: 2000 });
    } else {
      setTimeout(publishAnalytics, 100);
    }
  }

  _updateFinalPrice() {
    const priceEl = document.querySelector('.final-product-price');
    if (!priceEl) return;

    // Hide price if selection is not available
    if (!this.currentSelectionDetails.available) {
      priceEl.innerHTML = '';
      priceEl.style.display = 'none';
      return;
    }

    // Show price element
    priceEl.style.display = '';

    // Calculate total compare_at price
    const sizeComp = this.components.size[this.state.confirmed.size_option];
    const framingComp = this.components.framing[this.state.confirmed.size_option]?.[this.state.confirmed.framing_option];
    const borderComp = this.components.border[this.state.confirmed.size_option]?.[this.state.confirmed.border_option];

    let totalCompareAt = 0;
    totalCompareAt += (sizeComp?.compare_at_price > 0 ? sizeComp.compare_at_price : sizeComp?.price || 0);
    totalCompareAt += (framingComp?.compare_at_price > 0 ? framingComp.compare_at_price : framingComp?.price || 0);
    totalCompareAt += (borderComp?.compare_at_price > 0 ? borderComp.compare_at_price : borderComp?.price || 0);

    // Build pricing HTML
    let priceHTML = '';

    // Show compare_at price if it's higher than current price
    if (totalCompareAt > this.currentSelectionDetails.price) {
      priceHTML += `<span class="price-compare-at">${this._formatTotalPrice(totalCompareAt)}</span>`;
    }

    priceHTML += `<span class="price-current">${this._formatTotalPrice(this.currentSelectionDetails.price)}</span>`;

    const rawPercent = totalCompareAt > this.currentSelectionDetails.price
      ? (totalCompareAt - this.currentSelectionDetails.price) / totalCompareAt * 100
      : 0;
    const actualDiscountPercent = rawPercent > 0 ? Math.round(rawPercent / 5) * 5 : 0;
    if (this.variantConfigurators.discount_percent > 0 && actualDiscountPercent > 0) {
      const hasExpiry = this.variantConfigurators.discount_expiry_date || this.variantConfigurators.discount_expiry_hour;
      const badgeText = (this.variantConfigurators.ui_strings?.discount_badge || '%PERCENT%% off').replace('%PERCENT%', actualDiscountPercent);
      if (hasExpiry) {
        priceHTML += `<span class="price-countdown-badge"><span class="countdown-text">${badgeText}<span class="countdown-timer"> · --</span></span></span>`;
      } else {
        priceHTML += `<span class="price-countdown-badge"><span class="countdown-text">${badgeText}</span></span>`;
      }
    }

    // Add tier discount badge if compare_at price exceeds a threshold
    const tierConfig = this.variantConfigurators.tiered_discount_config;
    if (tierConfig && Object.keys(tierConfig).length > 0 && totalCompareAt > 0) {
      // Get tiers for current market - try multiple keys like delivery estimates
      const countryCode = this.variantConfigurators.country_code || '';
      const marketHandle = this.variantConfigurators.market_handle || 'default';
      const tiers = tierConfig[countryCode]
        || tierConfig[countryCode.toUpperCase()]
        || tierConfig[marketHandle]
        || tierConfig['default']
        || [];

      // Convert totalCompareAt from cents to currency units
      const compareAtValue = totalCompareAt / 100;

      // Find the highest tier threshold that's exceeded
      let extraDiscount = 0;
      for (const tier of tiers) {
        if (compareAtValue >= tier.threshold) {
          extraDiscount = tier.percentage;
        }
      }

      // Show badge if a tier is unlocked
      if (extraDiscount > 0) {
        priceHTML += `<span class="price-tier-badge">Extra -${extraDiscount}% in cart</span>`;
      }
    }

    priceEl.innerHTML = priceHTML;

    // Start countdown timer if needed
    const hasExpiry = this.variantConfigurators.discount_expiry_date || this.variantConfigurators.discount_expiry_hour;
    if (this.variantConfigurators.discount_percent > 0 && hasExpiry) {
      this._startCountdownTimer();
    }
  }

  _startCountdownTimer() {
    const timerEl = document.querySelector('.countdown-timer');
    if (!timerEl) return;

    const expiryDate = this.variantConfigurators.discount_expiry_date;
    const expiryHour = this.variantConfigurators.discount_expiry_hour;

    const updateTimer = () => {
      const now = new Date();

      if (expiryDate) {
        const target = new Date(expiryDate);
        const diffMs = target - now;

        if (diffMs <= 0) {
          // Expiry passed — hide countdown, badge stays as static "X% off"
          timerEl.style.display = 'none';
          clearInterval(this._countdownInterval);
          return;
        }

        if (diffMs > 24 * 60 * 60 * 1000) {
          // More than 24h — show static date
          const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const day = days[target.getDay()];
          const dd = String(target.getDate()).padStart(2, '0');
          const mm = String(target.getMonth() + 1).padStart(2, '0');
          timerEl.textContent = ` · ends ${day} ${dd}/${mm}`;
          return;
        }

        // Less than 24h — live countdown
        const totalSeconds = Math.floor(diffMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        timerEl.textContent = ` · ends in ${hours}h ${String(minutes).padStart(2, '0')}m`;
      } else if (expiryHour) {
        // Legacy hour-of-day mode
        const cutoff = new Date(now);
        cutoff.setHours(expiryHour, 0, 0, 0);
        if (cutoff <= now) cutoff.setDate(cutoff.getDate() + 1);
        const diffMs = cutoff - now;
        const totalMinutes = Math.floor(diffMs / 60000);
        const displayHours = Math.floor(totalMinutes / 60);
        const displayMinutes = totalMinutes % 60;
        timerEl.textContent = ` · ${displayHours}h ${String(displayMinutes).padStart(2, '0')}m`;
      }
    };

    updateTimer();

    if (this._countdownInterval) {
      clearInterval(this._countdownInterval);
    }
    this._countdownInterval = setInterval(updateTimer, 60000);
  }

  /**
   * Looks up Prodigi configuration based on selected options
   * Note: This lookup provides prodigi_sku, prodigi_wrap, and border_percent.
   * The prodigi_color comes from the frame color metaobject, not this lookup.
   * @param {string} size_option - The selected size option handle
   * @param {string} framing_option - The selected framing option handle
   * @param {string} border_option - The selected border option handle
   * @param {string} art_product - The art product type ('art-print' or 'canvas-print')
   * @returns {Object} Configuration object with prodigi_sku, prodigi_wrap, border_percent (and optional warning)
   */
  _lookupProdigiConfig(size_option, framing_option, border_option, art_product) {
    // Handle missing or invalid Prodigi config
    if (!this.prodigiConfig || !Array.isArray(this.prodigiConfig)) {
      console.warn('[ProdigiConfig] No valid config array available');
      return { warning: 'no-configuration-found' };
    }

    // Find all matching entries
    // Note: Do NOT convert box-frame to classic-frame here - that's only for mock lookups
    const matches = this.prodigiConfig.filter(entry => {
      const sizeMatch = entry.size_option === size_option;
      const framingMatch = entry.framing_option === framing_option;
      const borderMatch = entry.border_option === border_option;
      const artProductMatch = entry.art_product === art_product;

      return sizeMatch && framingMatch && borderMatch && artProductMatch;
    });

    // No matches found
    if (matches.length === 0) {
      return { warning: 'no-configuration-found' };
    }

    // Multiple matches found - return first with warning
    if (matches.length > 1) {
      return { ...matches[0], warning: 'multiple-configurations-found' };
    }

    // Single match found
    return matches[0];
  }

  _updateFormProperties() {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    const idInput = form.querySelector('input[name="id"]');
    if (idInput) { idInput.value = this.currentVariantId; }
    // Clear only the sku-picker's OWN option properties — preserve the
    // personalise design link written by personalise-canvas.js. On the
    // personalisable template both scripts share this cart form; a blanket
    // remove here wiped the customer's design reference (_design_id /
    // _rendered_image_url / _styled_photo_s3_key) on every selection sync,
    // so the order shipped with no personalised artwork and the print lab
    // tagged it missingS3keyInProduct / prodigiSubmitIssue.
    form.querySelectorAll('input[name^="properties["]').forEach(input => {
      const m = input.name.match(/^properties\[(.+)\]$/);
      if (m && SkuPicker.PERSONALISE_PROPERTY_KEYS.has(m[1])) return;
      input.remove();
    });
    if (this.currentSelectionDetails.available) {
        // Get presentational labels for each option
        const sizeOption = this.data.options.size.find(s => s.handle === this.state.confirmed.size_option);
        const framingOption = this.data.options.framing.find(f => f.handle === this.state.confirmed.framing_option);
        const borderOption = this.data.options.border.find(b => b.handle === this.state.confirmed.border_option);
        const frameColorOption = framingOption?.colors?.find(c => c.handle === this.state.confirmed.frame_color);

        // Lookup Prodigi configuration based on selected options
        // Get art_product from variant data
        const variantData = this.variantConfigurators.variants[this.currentVariantId];
        const artProduct = variantData?.artProduct || null;

        const prodigiConfig = this._lookupProdigiConfig(
            this.state.confirmed.size_option,
            this.state.confirmed.framing_option,
            this.state.confirmed.border_option,
            artProduct
        );


        // Build internal properties (with _ prefix)
        const internalProperties = {
            _size_option: this.state.confirmed.size_option,
            _framing_option: this.state.confirmed.framing_option,
            _border_option: this.state.confirmed.border_option,
            _frame_color: this.state.confirmed.frame_color,
            _sitewide_price: this.currentSelectionDetails.price  // Store price at add-to-cart for bundle discount detection
        };

        // Add artwork_s3_key from variant data (variantData already declared above)
        if (variantData && variantData.artworkS3Key) {
            internalProperties._artwork_s3_key = variantData.artworkS3Key;
        }

        // Add Prodigi SKU if present (required field)
        if (prodigiConfig.prodigi_sku) {
            internalProperties._prodigi_sku = prodigiConfig.prodigi_sku;
        }

        // Add prodigi_color from frame color metaobject if present (optional)
        if (frameColorOption && frameColorOption.prodigi_color) {
            internalProperties._prodigi_color = frameColorOption.prodigi_color;
        } else {
        }

        // Add prodigi_wrap if present in config (optional)
        if (prodigiConfig.prodigi_wrap) {
            internalProperties._prodigi_wrap = prodigiConfig.prodigi_wrap;
        }

        // Add border_percent if present in config (optional)
        if (prodigiConfig.border_percent) {
            internalProperties._border_percent = prodigiConfig.border_percent;
        }

        // Add warning field if configuration lookup had issues
        if (prodigiConfig.warning) {
            internalProperties._prodigi_warning = prodigiConfig.warning;
        }

        // Add selected mock image handle for cart rendering
        const selectedMockHandle = this._getSelectedMockHandle();
        if (selectedMockHandle) {
            internalProperties._mock_image_handle = selectedMockHandle;
        }

        // Build presentational properties (user-facing labels)
        const presentationalProperties = {};

        // Derive the branded style name from the confirmed selection so the
        // cart "Style" line is always consistent with the size/framing/border
        // actually being added. style-picker registers `resolveStyleName`;
        // fall back to the pushed `activeStyleName` when no resolver exists
        // (e.g. surgical config without the full style-picker).
        const styleName = typeof this.resolveStyleName === 'function'
            ? this.resolveStyleName(this.state.confirmed)
            : this.activeStyleName;

        if (styleName && sizeOption) {
            // New PDP: single combined "Style" property (named presets only).
            const bucketName = this._getSizeBucketName(sizeOption);
            const dims = this._formatSizeDimensions(sizeOption);
            const isUnframed = framingOption && framingOption.handle === 'unframed';
            presentationalProperties['Style'] = isUnframed
                ? `The ${bucketName} - Unframed, ${dims}`
                : `${styleName} ${bucketName}, ${dims}`;

            // Also emit conventional specs so checkout shows complete order details
            if (!isUnframed && framingOption) {
                const colorName = frameColorOption
                    ? (frameColorOption.short_name || frameColorOption.label)
                    : '';
                const framingType = framingOption.short_name || framingOption.label;
                const frameSuffix = this._t('frame_suffix', 'Frame');
                presentationalProperties['Frame'] = colorName
                    ? `${colorName} ${framingType} ${frameSuffix}`
                    : `${framingType} ${frameSuffix}`;
            }
            if (borderOption && borderOption.handle !== 'no-border') {
                presentationalProperties['Border'] = borderOption.label;
            }
        } else {
            // Fallback for custom configuration or missing style-picker
            if (sizeOption) {
                presentationalProperties['Size'] = this._formatSizeDimensions(sizeOption);
            }

            if (framingOption && framingOption.handle !== 'unframed') {
                const colorName = frameColorOption
                    ? (frameColorOption.short_name || frameColorOption.label)
                    : '';
                const framingType = framingOption.short_name || framingOption.label;
                const frameSuffix = this._t('frame_suffix', 'Frame');
                presentationalProperties['Frame'] = colorName
                    ? `${colorName} ${framingType} ${frameSuffix}`
                    : `${framingType} ${frameSuffix}`;
            }

            if (borderOption) {
                presentationalProperties['Border'] = borderOption.label;
            }
        }

        // Add all properties to form (internal first, then presentational)
        const allProperties = { ...internalProperties, ...presentationalProperties };

        for (const key in allProperties) {
            if (allProperties[key]) {
                const hiddenInput = document.createElement('input');
                hiddenInput.type = 'hidden';
                hiddenInput.name = `properties[${key}]`;
                hiddenInput.value = allProperties[key];
                form.appendChild(hiddenInput);
            }
        }
    }
    const addToCartButton = form.querySelector('[name="add"]');
    if (addToCartButton) {
        addToCartButton.disabled = !this.currentSelectionDetails.available;
        const textSpan = addToCartButton.querySelector('.add-to-cart-text__content');
        if (textSpan) {
            textSpan.textContent = this.currentSelectionDetails.available ? this._t('add_to_cart', 'Add to cart') : this._t('unavailable', 'Unavailable');
        }
    }
  }

  /**
   * Gets the handle of the currently selected mock based on current configuration.
   * Mirrors the mock selection logic from media-gallery.js
   *
   * @returns {string|null} - Mock handle or null
   */
  _getSelectedMockHandle() {
    // Get gallery data for current variant
    const galleryDataElement = document.getElementById('GalleryData');
    if (!galleryDataElement) return null;

    try {
      const allData = JSON.parse(galleryDataElement.textContent);
      const variantData = allData.variants?.[this.currentVariantId];

      if (!variantData || !variantData.mocks || variantData.mocks.length === 0) {
        // Full mocks not loaded yet — return null rather than defaultMocks.product
        // which may not match the current framing/size config. The cart template
        // falls back to the configurator's default_product_mock metafield when
        // _mock_image_handle is absent, which is safer than a wrong handle.
        return null;
      }

      // Build selected options array (same logic as media-gallery.js)
      const isCanvasProduct = variantData.artProduct === 'canvas-print';

      // Handle box-frame to classic-frame color aliasing
      let frameColorForLookup = this.state.confirmed.frame_color;
      if (frameColorForLookup && frameColorForLookup.startsWith('box-frame-')) {
        frameColorForLookup = frameColorForLookup.replace(/^box-frame-/, 'classic-frame-');
      }

      let selectedOptions;
      if (isCanvasProduct) {
        // Canvas: only match on frame color
        selectedOptions = [frameColorForLookup].filter(Boolean);
        if (this.state.confirmed.framing_option === 'unframed') {
          selectedOptions.push('unframed');
        }
      } else {
        // Art print: match on size, border, and frame color
        selectedOptions = [
          this.state.confirmed.size_option,
          this.state.confirmed.border_option,
          frameColorForLookup
        ].filter(Boolean);
        if (this.state.confirmed.framing_option === 'unframed') {
          selectedOptions.push('unframed');
        }
      }

      // Find best matching product mock
      const productMocks = variantData.mocks.filter(m => m.type === 'product');
      const bestMock = this._findBestMockMatch(productMocks, selectedOptions);

      return bestMock ? bestMock.handle : (variantData?.defaultMocks?.product || null);
    } catch (error) {
      console.error('[SkuPicker] Error getting mock handle:', error);
      return null;
    }
  }

  /**
   * Finds the best matching mock from an array based on selected options.
   *
   * @param {Array} mocks - Array of mock objects
   * @param {Array} selectedOptions - Array of selected option handles
   * @returns {Object|null} - Best matching mock or null
   */
  _findBestMockMatch(mocks, selectedOptions) {
    let bestMatch = null;
    let bestScore = 0;

    for (const mock of mocks) {
      if (!mock.options || !Array.isArray(mock.options)) continue;

      // Score is the number of selected options that match this mock's options
      const score = selectedOptions.filter(opt =>
        mock.options.includes(opt)
      ).length;

      if (score > bestScore) {
        bestScore = score;
        bestMatch = mock;
      }
    }

    return bestMatch;
  }
}

// Export SkuPicker to global scope for subclasses (BundleSkuPicker, PhotoPrintSkuPicker) to extend
window.SkuPicker = SkuPicker;

// Initialize immediately - no need to wait for DOMContentLoaded since script is deferred

// Deferred scripts already wait for DOM parsing to complete
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSkuPicker);
} else {
  // DOM already loaded, initialize immediately
  initSkuPicker();
}

function initSkuPicker() {
  // Check if this is a bundle product - if so, skip initialization
  // BundleSkuPicker will handle it instead
  const bundleData = document.getElementById('BundleComponentData');
  if (bundleData) return;

  const container = document.getElementById('sku-picker-container');
  const showPhotoUpload = container?.dataset.showPhotoUpload === 'true';

  // For photo upload products, wait for PhotoPrintSkuPicker to be loaded
  // (it will call initSkuPicker again after defining itself)
  if (showPhotoUpload && !window.PhotoPrintSkuPicker) {
    return;
  }

  // Use PhotoPrintSkuPicker for photo upload products, otherwise base SkuPicker
  if (showPhotoUpload && window.PhotoPrintSkuPicker) {
    window.skuPickerInstance = new PhotoPrintSkuPicker('sku-picker-container');
  } else {
    window.skuPickerInstance = new SkuPicker('sku-picker-container');
  }
}